﻿// ── State ────────────────────────────────────────────────────────────────────
const state = {
  currentStep: 1,
  foremanName: '',
  date: '',
  workersOnSite: '',
  project: '',
  submissionType: '',
  fields: {},
  photos: [],
  signature: null,
  allowedTypes: null,
  fromDashboard: false,  // true when type + project were pre-selected on dashboard
  sendToSafety: true,    // toggle on step 4 — send PDF to CSO
  _csoContact: null,     // { cso_email, cso_name } loaded for current project
  // Admin-only: when an admin submits on behalf of a foreman (e.g. counted
  // blocks on site themselves), these override the foreman_id / foreman_name
  // written to submissions, production_reports and time_entries. Null = submit
  // as the logged-in user (default for everyone, including admins).
  onBehalfOfForemanId: null,
  onBehalfOfForemanName: '',
};

// ── Bootstrap ─────────────────────────────────────────────────────────────────
// DOMContentLoaded just hands off to auth — initializeApp() is called by
// auth.js after a successful login (or valid existing session).
document.addEventListener('DOMContentLoaded', () => initAuth());

// Returns today as YYYY-MM-DD in the foreman's LOCAL timezone (not UTC).
// Using toISOString() would shift the date forward in any time zone east of UTC
// (and backward west of it after ~5pm local) — bad for Vancouver crews filling
// out a form in the evening.
function _todayLocal() {
  const d = new Date();
  return d.getFullYear() + '-' +
         String(d.getMonth() + 1).padStart(2, '0') + '-' +
         String(d.getDate()).padStart(2, '0');
}

// ── initializeApp ─────────────────────────────────────────────────────────────
// Called by auth.js once the user is authenticated and their profile is loaded.
function initializeApp() {
  const today = _todayLocal();
  const dateEl = document.getElementById('foremanDate');
  dateEl.value = today;
  dateEl.max   = today;   // browser-enforced: picker can't scroll past today
  state.date = today;

  // Pre-fill name from the logged-in profile so foreman doesn't have to type it
  if (window.currentProfile?.full_name) {
    state.foremanName = window.currentProfile.full_name;
    const nameEl = document.getElementById('foremanName');
    if (nameEl) nameEl.value = state.foremanName;
  }

  checkBackendHealth().then(online => {
    const dot = document.getElementById('backendStatus');
    if (dot) {
      dot.className = online ? 'status-dot online' : 'status-dot offline';
      dot.title = online ? 'Connected — submissions go to email' : 'Server offline — will download ZIP';
    }
  });

  attachNavListeners();

  document.getElementById('foremanName').addEventListener('input', e => { state.foremanName = e.target.value.trim(); });
  document.getElementById('foremanDate').addEventListener('input', e => { state.date = e.target.value; });

  // Load shared workers list for crew sign-in autofill (background, non-blocking)
  loadWorkersForAutofill().catch(err => console.warn('Workers autofill load failed:', err));

  // Admin-only: pre-warm the foremen picker cache so Step 3 renders instantly
  // when the admin chooses "Submit on behalf of …". Picker UI is injected on
  // Step 3 (see renderOnBehalfPicker) — Step 1 doesn't show it because the
  // dashboard fast-path bypasses Step 1.
  if (window.currentProfile?.role === 'admin') {
    _loadForemenForPickerCached().catch(err => console.warn('Foremen picker prewarm failed:', err));
  }

  showHomeDashboard();
}

// ── Admin "Submit on behalf of foreman" picker ───────────────────────────────
// Renders a dropdown at the top of Step 3 (admin only) listing every foreman +
// admin in `profiles`. Default is "Yourself" (no override). When the admin
// picks someone else, we stash the id/name on `state` so the DB writes for
// submissions / production_reports / time_entries land on the selected
// foreman's row in the dashboard, payroll and weekly-pair audit.
//
// Lives on Step 3 (not Step 1) because the dashboard fast-path skips Step 1 —
// Step 3 is the only step both flows visit, so this is the single source of
// truth for the picker UI.
let _foremenForPickerCache = null;
async function _loadForemenForPickerCached() {
  if (_foremenForPickerCache) return _foremenForPickerCache;
  const { data, error } = await sbClient
    .from('profiles')
    .select('id, full_name, role')
    .in('role', ['foreman', 'admin'])
    .order('full_name');
  if (error) { console.warn('Foremen list load failed:', error.message); return []; }
  _foremenForPickerCache = data || [];
  return _foremenForPickerCache;
}

async function renderOnBehalfPicker(container) {
  // Only render for admins — silent no-op for everyone else
  if (window.currentProfile?.role !== 'admin') return;
  // Don't render twice on the same step
  if (container.querySelector('#onBehalfPickerWrap')) return;

  const wrap = document.createElement('div');
  wrap.id = 'onBehalfPickerWrap';
  wrap.className = 'field-group on-behalf-wrap';
  wrap.innerHTML = `
    <label class="field-label" for="onBehalfForemanSelect">
      <span style="display:inline-flex;align-items:center;gap:6px">
        <span>👤 Submit on behalf of</span>
        <span style="opacity:.55;font-weight:500;font-size:12px;background:#f0f0f0;padding:2px 8px;border-radius:10px">ADMIN</span>
      </span>
    </label>
    <select class="field-input field-select" id="onBehalfForemanSelect">
      <option value="">— Yourself (${(window.currentProfile?.full_name || 'admin').replace(/"/g, '&quot;')}) —</option>
    </select>
    <div class="field-hint" style="margin-top:6px;color:#888;font-size:13px;line-height:1.4">
      Data, PDF and dashboard credit go to the selected foreman.
    </div>
  `;
  container.appendChild(wrap);

  const sel = wrap.querySelector('#onBehalfForemanSelect');
  const foremen = await _loadForemenForPickerCached();
  const myId = window.currentUser?.id;
  foremen.filter(p => p.id !== myId).forEach(p => {
    const opt = document.createElement('option');
    opt.value = p.id;
    opt.dataset.name = p.full_name || '';
    opt.textContent = (p.full_name || '(unnamed)') + (p.role === 'admin' ? ' · admin' : '');
    sel.appendChild(opt);
  });
  // Restore prior selection if user navigates back from review
  if (state.onBehalfOfForemanId) sel.value = state.onBehalfOfForemanId;

  sel.addEventListener('change', () => {
    const opt  = sel.options[sel.selectedIndex];
    const pid  = sel.value || null;
    const pname = opt?.dataset?.name || '';
    if (pid) {
      state.onBehalfOfForemanId   = pid;
      state.onBehalfOfForemanName = pname;
      state.foremanName           = pname;
    } else {
      state.onBehalfOfForemanId   = null;
      state.onBehalfOfForemanName = '';
      state.foremanName = window.currentProfile?.full_name || '';
    }
    // Keep Step 1's name input in sync (used by review screen + PDF)
    const nameEl = document.getElementById('foremanName');
    if (nameEl) nameEl.value = state.foremanName;
    // If the form already has a supervisor_name field rendered (autofill='foreman'),
    // refresh its visible value so the admin sees the change immediately.
    const sup = document.getElementById('field_supervisor_name');
    if (sup) { sup.value = state.foremanName; state.fields.supervisor_name = state.foremanName; }
  });
}

// Resolve the effective foreman_id / foreman_name for a submission. Used at
// every DB write spot so admin-on-behalf submissions land on the right row.
function _effectiveForemanId()   { return state.onBehalfOfForemanId   || window.currentUser?.id   || null; }
function _effectiveForemanName() { return state.onBehalfOfForemanName || window.currentProfile?.full_name || ''; }

// ── Load workers master list (for crew sign-in autocomplete) ──────────────────
// Builds a <datalist id="workersDatalist"> attached to the document. Crew name
// inputs reference it via list="workersDatalist" so the browser shows suggestions
// while the foreman types. Free text is still allowed (occasional contractor).
async function loadWorkersForAutofill() {
  const { data, error } = await sbClient
    .from('workers')
    .select('id, name, trade')
    .eq('active', true)
    .order('name');
  if (error) return;

  window.workersList = (data || []).map(w => w.name);
  // Full records (with id + trade) — used by the timesheet form's worker picker
  // so foremen can only choose real workers (no freehand names → no silently-lost rows).
  window.workersFull = data || [];

  // Build or refresh the datalist
  let dl = document.getElementById('workersDatalist');
  if (!dl) {
    dl = document.createElement('datalist');
    dl.id = 'workersDatalist';
    document.body.appendChild(dl);
  }
  dl.innerHTML = (data || []).map(w =>
    `<option value="${(w.name || '').replace(/"/g, '&quot;')}">${(w.trade || '').replace(/"/g, '&quot;')}</option>`
  ).join('');
}

// ── Admin panel ───────────────────────────────────────────────────────────────
function openAdminPanel() {
  window.location.href = 'admin.html';
}

// ── Home Dashboard ────────────────────────────────────────────────────────────

async function showHomeDashboard() {
  const homeScreen   = document.getElementById('homeScreen');
  const appDiv       = document.querySelector('#mainApp .app');
  const progressWrap = document.querySelector('.progress-wrap');
  const navBar       = document.querySelector('.nav-bar');

  if (homeScreen)   homeScreen.style.display  = 'block';
  if (appDiv)       appDiv.style.display      = 'none';
  if (progressWrap) progressWrap.style.display = 'none';
  if (navBar)       navBar.style.display      = 'none';

  state.allowedTypes  = null;
  state.fromDashboard = false;

  // Greeting
  const hour  = new Date().getHours();
  const greet = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const first = window.currentProfile?.full_name?.split(' ')[0] || '';
  const greetEl = document.getElementById('dashGreeting');
  if (greetEl) greetEl.textContent = `${greet}${first ? ', ' + first : ''}`;

  // Avatar — initial as immediate fallback, then load deterministic image
  const av = document.getElementById('dashAvatarBtn');
  if (av) {
    av.textContent = (first[0] || '?').toUpperCase();
    applyUserAvatar(av);
  }

  // Admin section toggle
  const adminSection = document.getElementById('dashAdminSection');
  if (adminSection) adminSection.hidden = window.currentProfile?.role !== 'admin';

  // Populate project selector
  const projSel = document.getElementById('dashProjectSelect');
  if (projSel) {
    const projects = window.userProjects || [];
    const prev = projSel.value; // keep previous selection across returns
    projSel.innerHTML = '<option value="">— Select a project to begin —</option>';
    projects.forEach(p => {
      const o = document.createElement('option');
      o.value = p; o.textContent = p;
      projSel.appendChild(o);
    });
    // Restore previous selection or use state.project
    const restore = prev || state.project || '';
    if (restore && projects.includes(restore)) projSel.value = restore;
    // Auto-select if only one project assigned
    if (!projSel.value && projects.length === 1) projSel.value = projects[0];
    projSel.onchange = () => { state.project = projSel.value; };
    state.project = projSel.value;
  }

  // Wire data-type item cards
  document.querySelectorAll('.dash-item[data-type]').forEach(el => {
    el.onclick = () => startSpecificFormFlow(el.dataset.type);
  });

  // Wire data-action cards (delivery, documents, admin)
  document.querySelectorAll('[data-action]').forEach(el => {
    el.onclick = () => handleDashAction(el.dataset.action);
  });

  // Tailgate banner
  const tailgate = await checkTailgateToday().catch(() => null);
  const alert = document.getElementById('dashTailgateAlert');
  if (alert) alert.hidden = !!tailgate;

  // Tailgate streak banner — non-blocking, only renders if streak >= 3
  loadTailgateStreak().then(renderTailgateStreak).catch(err => console.warn('streak load failed:', err));

  // Company Resources section (Safety Manual + other shared docs).
  // Background, non-blocking — the rest of the dashboard renders without it.
  loadCompanyResources().catch(err => console.warn('Company resources load failed:', err));

  // Motivational phrase (random, changes each visit)
  renderMotivationalPhrase();
}

// ── Company Resources (Safety Manual + future shared docs) ────────────────────
// One card per active row in company_documents. Click opens the PDF in a new
// tab (the bucket is public so no signed-URL round trip needed). Rows without
// a file_url yet (admin hasn't uploaded the file) render as disabled.
async function loadCompanyResources() {
  const sec   = document.getElementById('dashResourcesSection');
  const label = document.getElementById('dashResourcesLabel');
  if (!sec) return;

  const { data, error } = await sbClient
    .from('company_documents')
    .select('id, slug, title, description, icon, file_url, file_path, version')
    .eq('active', true)
    .order('sort_order', { ascending: true });

  if (error) { console.warn('company_documents load failed:', error.message); return; }
  const docs = data || [];
  if (!docs.length) { sec.hidden = true; if (label) label.hidden = true; return; }

  sec.innerHTML = docs.map(d => {
    const hasFile = !!(d.file_url || d.file_path);
    const ready = hasFile ? '' : ' style="opacity:.55;cursor:not-allowed"';
    const desc  = hasFile
      ? (d.description || 'Tap to view')
      : 'Not yet uploaded by admin';
    return `
      <button class="dash-card dash-card-doc accent-gray" data-doc-id="${d.id}"${ready}>
        <span style="font-size:22px">${d.icon || '📘'}</span>
        <div>
          <div class="dash-card-label">${(d.title || '').replace(/</g, '&lt;')}</div>
          <div class="dash-card-desc">${(desc || '').replace(/</g, '&lt;')}</div>
        </div>
        <span class="dash-card-arrow">›</span>
      </button>`;
  }).join('');

  sec.hidden = false;
  if (label) label.hidden = false;

  sec.querySelectorAll('button[data-doc-id]').forEach(btn => {
    btn.onclick = () => {
      const id = btn.dataset.docId;
      const doc = docs.find(d => d.id === id);
      if (!doc) return;
      if (doc.file_url) {
        window.open(doc.file_url, '_blank', 'noopener');
        return;
      }
      if (doc.file_path) {
        // Fallback: derive public URL from path (in case file_url wasn't cached)
        const { data: pub } = sbClient.storage.from('company-docs').getPublicUrl(doc.file_path);
        if (pub?.publicUrl) window.open(pub.publicUrl, '_blank', 'noopener');
        return;
      }
      showToast('This document has not been uploaded yet — please contact admin.', 'warning');
    };
  });
}

// Check if a Daily Tailgate was submitted today for this user
// Count consecutive days the foreman has submitted a Daily Tailgate.
// Looks back up to 90 days. If today hasn't been done yet, doesn't penalize —
// starts counting from yesterday backward. Returns { streak, lastDateStr }.
async function loadTailgateStreak() {
  const uid = window.currentUser?.id;
  if (!uid) return { streak: 0 };

  const since = new Date();
  since.setDate(since.getDate() - 90);
  const { data, error } = await sbClient.from('submissions')
    .select('submitted_at')
    .eq('foreman_id', uid)
    .eq('submission_type', 'Daily Tailgate')
    .gte('submitted_at', since.toISOString())
    .order('submitted_at', { ascending: false });

  if (error) { console.warn('streak query:', error.message); return { streak: 0 }; }
  if (!data || data.length === 0) return { streak: 0 };

  // Collect distinct LOCAL dates the foreman submitted on
  const localDate = (iso) => {
    const d = new Date(iso);
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  };
  const dates = new Set(data.map(r => localDate(r.submitted_at)));

  let streak = 0;
  let cursor = new Date();
  const todayStr = _todayLocal();

  // If today not done yet, start counting from yesterday (no penalty mid-day)
  if (!dates.has(todayStr)) cursor.setDate(cursor.getDate() - 1);

  for (let i = 0; i < 90; i++) {
    const dStr = cursor.getFullYear() + '-' + String(cursor.getMonth() + 1).padStart(2, '0') + '-' + String(cursor.getDate()).padStart(2, '0');
    if (dates.has(dStr)) {
      streak++;
      cursor.setDate(cursor.getDate() - 1);
    } else {
      break;
    }
  }

  return { streak, lastDateStr: localDate(data[0].submitted_at), todayDone: dates.has(todayStr) };
}

function renderTailgateStreak(info) {
  const el = document.getElementById('dashStreakBanner');
  if (!el) return;
  const { streak, todayDone } = info;
  if (!streak || streak < 3) { el.hidden = true; el.innerHTML = ''; return; }

  // Visual scaling: more fire for longer streaks
  const fires = streak >= 30 ? '🔥🔥🔥🔥' : streak >= 14 ? '🔥🔥🔥' : streak >= 7 ? '🔥🔥' : '🔥';
  const tier  = streak >= 30 ? 'legend'  : streak >= 14 ? 'roll'    : streak >= 7 ? 'streak' : 'streak';
  const msg = todayDone
    ? `<strong>${fires} ${streak}-day Daily Tailgate ${tier}!</strong> <span style="opacity:.85">Keep going — see you tomorrow.</span>`
    : `<strong>${fires} ${streak}-day Daily Tailgate ${tier}!</strong> <span style="opacity:.85">Don't break it — complete today's tailgate.</span>`;

  el.hidden = false;
  el.innerHTML = `
    <div class="dash-streak-content">
      <span class="dash-streak-text">${msg}</span>
      ${todayDone ? '' : '<button class="dash-streak-btn" onclick="startSpecificFormFlow(\'Daily Tailgate\')">Complete →</button>'}
    </div>`;
}

async function checkTailgateToday() {
  const uid = window.currentUser?.id;
  if (!uid) return false;
  // Use local Vancouver date — toISOString() returns UTC which rolls over at
  // 4-5pm local and would hide today's tailgate too early.
  // Column is `submitted_at` (not `created_at`) — was throwing 400s.
  const todayStr = _todayLocal();
  const { data, error } = await sbClient.from('submissions')
    .select('id')
    .eq('foreman_id', uid)
    .eq('submission_type', 'Daily Tailgate')
    .gte('submitted_at', todayStr + 'T00:00:00')
    .lte('submitted_at', todayStr + 'T23:59:59')
    .limit(1);
  if (error) { console.warn('checkTailgateToday:', error.message); return false; }
  return (data && data.length > 0) ? data[0] : null;
}

// ── Form-type icons — Lucide, mirroring the home dashboard tiles ─────────────
// (form-configs.js still carries the legacy emoji as a fallback for any type
// missing here; the MutationObserver in index.html converts data-lucide tags.)
const FORM_TYPE_LUCIDE = {
  'Daily Tailgate':           'shield-check',
  'Weekly Toolbox Talk':      'megaphone',
  'Incident Report':          'triangle-alert',
  'Telehandler Inspection':   'construction',
  'Forklift Inspection':      'package',
  'E-Pallet Jack Inspection': 'truck',
  'Scaffolding Inspection':   'layers',
  'QAQC - Foreman':           'clipboard-check',
  'Field QAQC - ITP':         'clipboard-list',
  'Site Photos Only':         'camera',
  'Weekly Timesheet':         'clock',
  'Production Report':        'bar-chart-3',
  'Extra Work Order':         'hammer',
  'Hazard Observation':       'eye',
};
function formTypeIconHtml(typeId) {
  const name = FORM_TYPE_LUCIDE[typeId];
  return name ? `<span class="ip-ic"><i data-lucide="${name}"></i></span>` : '';
}

// ── Start form flow for a specific pre-selected type ──────────────────────────
// Project + type are already known from the dashboard — jump straight to the form.
async function startSpecificFormFlow(type) {
  const projSel = document.getElementById('dashProjectSelect');
  const project = projSel?.value || '';

  if (!project) {
    showToast('Please select a project first ↑', 'warning');
    if (projSel) {
      projSel.focus();
      projSel.classList.add('dash-project-bar-select--pulse');
      setTimeout(() => projSel.classList.remove('dash-project-bar-select--pulse'), 900);
    }
    return;
  }

  state.project        = project;
  state.submissionType = type;
  state.allowedTypes   = [type];
  state.fromDashboard  = true;
  state.photos         = [];
  state._pendingDraft  = null;
  state._draftSavedAt  = null;

  // Pre-load draft for draftable forms before rendering
  if (DRAFTABLE_FORMS.includes(type)) {
    const draft = await loadDraftData(type);
    if (draft) {
      state._pendingDraft = draft.form_data;
      state._draftSavedAt = draft.updated_at;
    }
  }

  const typeGrid = document.getElementById('typeGrid');
  if (typeGrid) typeGrid.innerHTML = '';

  const homeScreen   = document.getElementById('homeScreen');
  const appDiv       = document.querySelector('#mainApp .app');
  const progressWrap = document.querySelector('.progress-wrap');
  const navBar       = document.querySelector('.nav-bar');

  if (homeScreen)   homeScreen.style.display  = 'none';
  if (appDiv)       appDiv.style.display      = '';
  if (progressWrap) progressWrap.style.display = '';
  if (navBar)       navBar.style.display      = '';

  state.currentStep = 3;
  renderStep(3);
  updateProgressBar(3);
  updateNavButtons(3);
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

function handleDashAction(action) {
  switch (action) {
    case 'delivery':       return openDeliveryModal();
    case 'equipment':      return openEquipmentOverlay();
    case 'documents':      return openDocsPage();
    case 'admin':          return openAdminPanel();
    case 'certifications': return openCertificationsPage();
  }
}

// ── Inner-page navigation ─────────────────────────────────────────────────────
// Hides home + form screens, shows the requested full-page div.
function showInnerPage(pageId) {
  const homeScreen   = document.getElementById('homeScreen');
  const appDiv       = document.querySelector('#mainApp .app');
  const progressWrap = document.querySelector('.progress-wrap');
  const navBar       = document.querySelector('.nav-bar');
  if (homeScreen)   homeScreen.style.display   = 'none';
  if (appDiv)       appDiv.style.display       = 'none';
  if (progressWrap) progressWrap.style.display = 'none';
  if (navBar)       navBar.style.display       = 'none';
  document.querySelectorAll('.inner-page').forEach(p => p.style.display = 'none');
  const page = document.getElementById(pageId);
  if (page) { page.style.display = 'flex'; }
  window.scrollTo({ top: 0, behavior: 'instant' });
}

function goHome() {
  document.querySelectorAll('.inner-page').forEach(p => p.style.display = 'none');
  showHomeDashboard();
}

// ── Project Documents full page ───────────────────────────────────────────────
async function openDocsPage() {
  const project = state.project;
  if (!project) {
    showToast('Please select a project first ↑', 'warning');
    const projSel = document.getElementById('dashProjectSelect');
    if (projSel) {
      projSel.focus();
      projSel.classList.add('dash-project-bar-select--pulse');
      setTimeout(() => projSel.classList.remove('dash-project-bar-select--pulse'), 900);
    }
    return;
  }

  showInnerPage('docsPage');
  const projEl = document.getElementById('docsPageProject');
  if (projEl) projEl.textContent = project;
  // Also hide the form strip (not needed on inner pages)
  const formNav = document.getElementById('formPageNav');
  if (formNav) formNav.style.display = 'none';

  const listEl = document.getElementById('docsPageList');
  if (listEl) listEl.innerHTML = '<div class="docs-loading">Loading…</div>';

  const docs = await loadProjectDocuments(project);
  if (!listEl) return;

  if (!docs.length) {
    listEl.innerHTML = '<div class="docs-empty">No documents uploaded for this project yet.</div>';
    return;
  }

  const SECTIONS = [
    { key: 'site_photo',    label: 'Site Photos',    icon: '📸', isPhotos: true  },
    { key: 'change_order',  label: 'Change Orders',  icon: '📋', isPhotos: false },
    { key: 'rfi',           label: 'RFIs',           icon: '🔄', isPhotos: false },
    { key: 'drawing',       label: 'Drawings',       icon: '📐', isPhotos: false },
    { key: 'submittal',     label: 'Submittals',     icon: '📩', isPhotos: false },
    { key: 'specification', label: 'Specifications', icon: '📑', isPhotos: false },
    { key: 'general',       label: 'General',        icon: '📄', isPhotos: false },
  ];

  const byType = {};
  for (const d of docs) {
    const k = d.type || 'general';
    if (!byType[k]) byType[k] = [];
    byType[k].push(d);
  }

  const s = str => String(str ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');

  let html = '';
  for (const sec of SECTIONS) {
    const items = byType[sec.key];
    if (!items?.length) continue;

    html += `<div class="fdoc-section">
      <div class="fdoc-section-hdr">
        <span>${sec.icon}</span>
        <span class="fdoc-section-title">${sec.label}</span>
        <span class="fdoc-count">${items.length}</span>
      </div>`;

    if (sec.isPhotos) {
      html += `<div class="fdoc-photo-grid">`;
      for (const d of items) {
        html += `<button class="fdoc-photo-card" type="button"
          onclick="openDocLightbox('${d.file_url.replace(/'/g,"\\'")}','${s(d.title)}','image')">
          <img class="fdoc-photo-thumb" src="${d.file_url}" alt="${s(d.title)}" loading="lazy">
          <div class="fdoc-photo-cap">${s(d.title)}</div>
        </button>`;
      }
      html += `</div>`;
    } else {
      for (const d of items) {
        html += `<button class="fdoc-row" type="button"
          onclick="openDocLightbox('${d.file_url.replace(/'/g,"\\'")}','${s(d.title)}','doc')">
          <div class="fdoc-row-info">
            <div class="fdoc-row-title">${s(d.title)}</div>
            ${d.notes ? `<div class="fdoc-row-notes">${s(d.notes)}</div>` : ''}
            <div class="fdoc-row-date">${new Date(d.created_at).toLocaleDateString()}</div>
          </div>
          <span class="fdoc-row-arrow">↗</span>
        </button>`;
      }
    }

    html += `</div>`;
  }

  listEl.innerHTML = html || '<div class="docs-empty">No documents uploaded for this project yet.</div>';
}

// ── Document / Photo Viewer ───────────────────────────────────────────────────
// type: 'image' → fullscreen lightbox with pinch-to-zoom
//       'doc'   → opens in real browser (Safari/Chrome) with full native zoom
function openDocLightbox(url, title, type) {
  // PDFs go straight to Safari (window.open). The iOS-Safari-in-iframe path
  // we tried before only renders page 1, no scroll, no real zoom — same on
  // most browsers. PDF.js gave us deep zoom but crashed memory on big
  // drawings. Opening in Safari directly hands the file to iOS PDFKit,
  // which is the same engine WhatsApp uses: full pinch-zoom, multi-page
  // scroll, mark-up, share — and after the foreman is done, "Done" brings
  // them right back to the PWA.
  if (type === 'doc') {
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }

  // Images keep the in-app fullscreen lightbox (with native pinch-zoom on the <img>)
  document.getElementById('docLightboxOverlay')?.remove();
  const safeTitle = (title || '').replace(/</g, '&lt;');
  const safeUrl   = url.replace(/"/g, '&quot;');

  const overlay = document.createElement('div');
  overlay.id = 'docLightboxOverlay';
  overlay.className = 'doc-lightbox-overlay';
  overlay.innerHTML = `
    <div class="doc-lightbox-bar">
      <span class="doc-lightbox-title">${safeTitle}</span>
      <div class="doc-lightbox-actions">
        <a class="doc-lightbox-btn" href="${safeUrl}" target="_blank" rel="noopener noreferrer" download>⬇ Save</a>
        <button class="doc-lightbox-btn" onclick="document.getElementById('docLightboxOverlay').remove()">✕ Close</button>
      </div>
    </div>
    <div class="doc-lightbox-body">
      <img class="doc-lightbox-img" src="${safeUrl}" alt="${safeTitle}">
    </div>`;

  overlay.addEventListener('click', e => {
    if (e.target === overlay) overlay.remove();
  });

  document.body.appendChild(overlay);
}

// ── Motivational Phrases ──────────────────────────────────────────────────────
const MOTIVATIONAL_PHRASES = [
  '🧱 Every brick laid is a step closer to something great.',
  '💪 Hard work never lies — the wall speaks for itself.',
  '🏗️ Built by hand, built to last.',
  '🔨 The best crews don\'t just build structures, they build pride.',
  '⚒️ Masonry is the art of turning stone into legacy.',
  '🧱 One brick at a time — that\'s how monuments are made.',
  '💼 Sweat today, stand tall in front of what you built tomorrow.',
  '🏆 Excellence isn\'t an accident — it\'s laid course by course.',
  '🔥 The toughest jobs create the toughest crews.',
  '🧱 A plumb line never lies — neither does honest work.',
  '⚡ Work hard in silence. Let the finished wall make the noise.',
  '🤝 Great sites are built by great teams.',
  '🏗️ Safety first, quality always, pride in every joint.',
  '💪 You don\'t build walls with shortcuts — and you don\'t build a reputation that way either.',
  '🧱 The foundation you lay today is the legacy you leave tomorrow.',
  '🌟 A craftsman\'s hands tell stories no words can match.',
  '🔨 Precision separates a good job from a great one.',
  '⚒️ Every square foot of this city has a bricklayer behind it.',
  '🏆 Show up. Work hard. Go home proud.',
  '💼 The difference between ordinary and extraordinary is the extra effort.',
  '🧱 Cold mornings, hot work — that\'s the life of a true tradesperson.',
  '🔥 Brick by brick, day by day — something permanent grows.',
  '💪 Your hands are building something that will outlast all of us.',
  '⚡ Character is what you build when no one\'s watching the joint.',
  '🤝 A safe crew is a strong crew.',
  '🏗️ Trade skills are not just a job — they\'re a craft worth being proud of.',
  '🧱 The mortar sets, the wall stands, the crew moves on to the next challenge.',
  '🌟 Quality is remembered long after the deadline is forgotten.',
  '🔨 Do it right the first time — rework costs more than care.',
  '⚒️ No elevator to the top of a scaffold — you climb it one rung at a time.',
  '🏆 A tradesperson\'s legacy is built in stone, brick, and hard work.',
  '💼 The best job site is a safe job site.',
  '🔥 Rain or shine — the work gets done.',
  '💪 Built with local hands. Standing for generations.',
  '🧱 Steady hands, sharp eyes, strong back — that\'s the trade.',
];

function renderMotivationalPhrase() {
  const el = document.getElementById('dashWeather');
  if (!el) return;
  const phrase = MOTIVATIONAL_PHRASES[Math.floor(Math.random() * MOTIVATIONAL_PHRASES.length)];
  el.hidden    = false;
  el.className = 'dash-motto';
  el.textContent = phrase;
}

// ── Navigation ───────────────────────────────────────────────────────────────
// Shared back handler — called by both the header strip button and btnBack
function _handleBack() {
  if (state.fromDashboard && state.currentStep === 3) {
    showHomeDashboard();
  } else if (state.currentStep > 1) {
    goToStep(state.currentStep - 1);
  } else {
    showHomeDashboard();
  }
}

function attachNavListeners() {
  document.getElementById('btnBack').addEventListener('click', _handleBack);
  document.getElementById('formBtnBack').addEventListener('click', _handleBack);
  document.getElementById('btnNext').addEventListener('click', async () => {
    if (!validateStep(state.currentStep)) return;
    // Site Photos submits directly from step 3 (no PDF, no step 4)
    if (state.submissionType === 'Site Photos Only' && state.currentStep === 3) { handleSubmit(); return; }
    if (state.currentStep === 4) { handleSubmit(); return; }

    // For draftable forms, pre-load draft before rendering step 3
    if (state.currentStep === 2 && DRAFTABLE_FORMS.includes(state.submissionType)) {
      state._pendingDraft = null;
      state._draftSavedAt = null;
      const draft = await loadDraftData(state.submissionType);
      if (draft) {
        state._pendingDraft = draft.form_data;
        state._draftSavedAt = draft.updated_at;
      }
    }
    goToStep(state.currentStep + 1);
  });
}

function goToStep(n) {
  state.currentStep = n;
  renderStep(n);
  updateProgressBar(n);
  updateNavButtons(n);
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

function updateProgressBar(step) {
  for (let i = 1; i <= 4; i++) {
    const el = document.getElementById(`stepDot${i}`);
    if (!el) continue;
    el.className = 'step-dot' + (i < step ? ' done' : i === step ? ' active' : '');
  }
  const bar = document.getElementById('progressFill');
  if (bar) bar.style.width = `${((step - 1) / 3) * 100}%`;
}

function updateNavButtons(step) {
  const back = document.getElementById('btnBack');
  const next = document.getElementById('btnNext');
  const formNav = document.getElementById('formPageNav');
  const formBackBtn = document.getElementById('formBtnBack');

  // Bottom back button is replaced by top strip — hide it
  back.style.display = 'none';

  // Show top back strip with correct label
  const backLabel = step === 1 ? '← Home' : '← Back';
  if (formNav)     formNav.style.display = 'flex';
  if (formBackBtn) formBackBtn.textContent = backLabel;

  const isSitePhotosSubmit = (step === 3 && state.submissionType === 'Site Photos Only');
  const isSubmitStep = step === 4 || isSitePhotosSubmit;
  // Site Photos flow uploads only — label as such. All other forms generate
  // PDF + send email + record submission, so "Submit Form" is more accurate.
  next.textContent = isSubmitStep
    ? (isSitePhotosSubmit ? '📤 Upload Photos' : '📤 Submit Form')
    : 'Next →';
  next.className   = isSubmitStep ? 'btn btn-submit' : 'btn btn-next';
  next.style.display = '';
}

// ── Step Rendering ───────────────────────────────────────────────────────────
function renderStep(n) {
  document.querySelectorAll('.step-panel').forEach(p => p.classList.remove('active'));
  const panel = document.getElementById(`step${n}`);
  if (panel) panel.classList.add('active');
  if (n === 2) renderStep2();
  if (n === 3) renderStep3();
  if (n === 4) renderStep4();
}

// ── Step 2 ───────────────────────────────────────────────────────────────────
function renderStep2() {
  const sel = document.getElementById('projectSelect');
  if (sel) {
    // Projects come from Supabase (loaded at login into window.userProjects)
    const userProjects = window.userProjects || [];
    sel.innerHTML = '<option value="">Select project...</option>';
    userProjects.forEach(p => {
      const o = document.createElement('option');
      o.value = p; o.textContent = p;
      if (p === state.project) o.selected = true;
      sel.appendChild(o);
    });
    // Reset project if it's no longer in the list
    if (state.project && !userProjects.includes(state.project)) {
      state.project = '';
    }
    sel.value = state.project || '';
    sel.onchange = e => { state.project = e.target.value; };

    // Documents button — opens drawer with docs for the selected project
    const docsHintId = 'projectDocsHint';
    let docsHint = document.getElementById(docsHintId);
    if (!docsHint) {
      docsHint = document.createElement('button');
      docsHint.id = docsHintId;
      docsHint.type = 'button';
      docsHint.className = 'docs-trigger-btn';
      docsHint.innerHTML = '📄 View Project Documents';
      docsHint.addEventListener('click', () => {
        const proj = document.getElementById('projectSelect').value;
        if (proj) showDocumentsDrawer(proj);
        else showToast('Select a project first', 'warning');
      });
      sel.parentNode.appendChild(docsHint);
    }
  }

  const grid = document.getElementById('typeGrid');
  if (grid && grid.children.length === 0) {
    const typesToShow = state.allowedTypes
      ? SUBMISSION_TYPES.filter(t => state.allowedTypes.includes(t.id))
      : SUBMISSION_TYPES;
    typesToShow.forEach(t => {
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'type-card' + (state.submissionType === t.id ? ' selected' : '');
      card.innerHTML = `<span class="type-icon">${FORM_TYPE_LUCIDE[t.id] ? `<i data-lucide="${FORM_TYPE_LUCIDE[t.id]}"></i>` : t.icon}</span><span class="type-name">${t.id}</span><span class="type-desc">${t.desc}</span>`;
      card.addEventListener('click', () => {
        document.querySelectorAll('.type-card').forEach(c => c.classList.remove('selected'));
        card.classList.add('selected');
        state.submissionType = t.id;
      });
      grid.appendChild(card);
    });
  }
}

// ── Step 3: Form ──────────────────────────────────────────────────────────────
// ── PDF dependency lazy-loader ───────────────────────────────────────────────
// jsPDF (140 KB) + autotable (30 KB) + our pdf-generator.js (128 KB) + logo.js
// (15 KB) total ~313 KB the foreman doesn't need to download until they hit
// Submit. We pre-warm the cache when they reach Step 3 (which they spend
// minutes filling out), so by the time they hit Submit the deps are ready.
let _pdfDepsPromise = null;
function _ensurePdfDeps() {
  if (_pdfDepsPromise) return _pdfDepsPromise;
  // Read the cache-bust version from the existing app.js script tag in the DOM
  // so the lazy-loaded modules share the same cache key as the rest of the app.
  // This avoids each page load generating a fresh URL (timestamp fallback) that
  // breaks the SW cache and re-downloads the deps unnecessarily.
  const v = document.querySelector('script[src*="js/app.js"]')?.src.match(/v=(\d+)/)?.[1]
         || window.APP_VERSION
         || '0';
  const load = (src) => new Promise((resolve, reject) => {
    // Skip if already in DOM (e.g. CDN script loaded twice = no-op)
    if (document.querySelector(`script[data-pdf-dep="${src}"]`)) return resolve();
    const s = document.createElement('script');
    s.src = src; s.async = false;
    s.dataset.pdfDep = src;
    s.onload  = resolve;
    s.onerror = () => reject(new Error('Failed to load ' + src));
    document.head.appendChild(s);
  });

  _pdfDepsPromise = (async () => {
    // jsPDF first — autotable + our pdf-generator depend on it
    await load('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');
    // Then everything else can load in parallel
    await Promise.all([
      load('https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.7.1/jspdf.plugin.autotable.min.js'),
      load(`js/logo.min.js?v=${v}`),
      load(`js/pdf-generator.min.js?v=${v}`),
    ]);
  })().catch(err => {
    // Reset the promise on failure so a retry can try again
    _pdfDepsPromise = null;
    throw err;
  });
  return _pdfDepsPromise;
}

function renderStep3() {
  // Pre-warm PDF deps in the background. Foreman is about to spend minutes
  // filling the form, so this is a great time to fetch the heavy bits.
  _ensurePdfDeps().catch(err => console.warn('PDF deps pre-warm failed:', err));

  // Always restore the standard photo section first (may have been hidden by Site Photos flow)
  const _ps = document.querySelector('.photo-section');
  if (_ps) _ps.style.display = '';

  // Site Photos has its own simplified flow — bypass the generic form builder
  if (state.submissionType === 'Site Photos Only') {
    renderSitePhotosStep();
    return;
  }

  const titleEl = document.getElementById('formTypeTitle');
  const typeInfo = SUBMISSION_TYPES.find(t => t.id === state.submissionType);
  if (titleEl) {
    const ic = formTypeIconHtml(state.submissionType);
    if (ic) titleEl.innerHTML = ic + state.submissionType;   // fixed list — safe to inline
    else titleEl.textContent = `${typeInfo?.icon || ''} ${state.submissionType}`;
  }

  const container = document.getElementById('dynamicFields');
  if (!container) return;
  container.innerHTML = '';
  state.fields    = {};        // clear stale field data on every form switch
  state.signature   = null;
  state.gcSignature = null;
  window._signaturePad   = null;
  window._gcSignaturePad = null;

  // ── Restore draft if available ────────────────────────────────────────────
  if (state._pendingDraft) {
    Object.assign(state.fields, state._pendingDraft);
    state._pendingDraft = null;
  }

  // ── Draft banner (draftable forms only) ───────────────────────────────────
  if (DRAFTABLE_FORMS.includes(state.submissionType)) {
    const savedAt = state._draftSavedAt
      ? new Date(state._draftSavedAt).toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
      : null;

    const banner = document.createElement('div');
    banner.className = 'draft-banner';
    banner.innerHTML = savedAt
      ? `<span class="draft-banner-msg">📂 Draft restored · <span id="draftSavedAt">Last saved ${savedAt}</span></span>
         <div class="draft-banner-actions">
           <button type="button" class="draft-save-btn" onclick="saveDraft()">💾 Save Progress</button>
           <button type="button" class="draft-clear-btn" onclick="clearDraftAndReset()">✕ Clear</button>
         </div>`
      : `<span class="draft-banner-msg">✨ Auto-saving every 30 seconds · <span id="draftSavedAt">Not saved yet</span></span>
         <button type="button" class="draft-save-btn" onclick="saveDraft()">💾 Save Now</button>`;
    container.appendChild(banner);
    state._draftSavedAt = null;
    // Start the auto-save heartbeat (cleared in resetApp / on submit success)
    _startAutoSaveDraft();
  }

  // ── Admin "Submit on behalf of foreman" picker (admin only, no-op for others) ─
  // Injected at the top of Step 3 because the dashboard fast-path skips Step 1.
  renderOnBehalfPicker(container);

  // ── Date field (always first — was previously in step 1) ──────────────────
  const dateWrapper = document.createElement('div');
  dateWrapper.className = 'field-group';
  const dateLbl = document.createElement('label');
  dateLbl.className = 'field-label';
  dateLbl.htmlFor = 'formStepDate';
  dateLbl.textContent = 'Date *';
  const dateInput = document.createElement('input');
  dateInput.type = 'date';
  dateInput.className = 'field-input';
  dateInput.id = 'formStepDate';
  dateInput.value = state.date;
  dateInput.addEventListener('change', e => { state.date = e.target.value; });
  dateWrapper.appendChild(dateLbl);
  dateWrapper.appendChild(dateInput);
  container.appendChild(dateWrapper);

  // Pre-fill defaults for Daily Tailgate
  if (state.submissionType === 'Daily Tailgate') applyDailyTailgateDefaults();

  const fields = FORM_FIELDS[state.submissionType] || [];
  fields.forEach(field => renderField(field, container));

  setupPhotoUpload();
}

// ── Daily Tailgate — pre-fill defaults ───────────────────────────────────────
function applyDailyTailgateDefaults() {
  // Checkboxes: standard items already checked based on daily masonry operations
  state.fields.items_reviewed = { ...DEFAULT_TAILGATE_CHECKED };

  // FLRA: pre-select the first N core tasks (rest stay unchecked)
  state.fields.flra = DEFAULT_FLRA_TASKS.map((t, i) => ({
    ...t,
    hazards: [...t.hazards],
    included: i < DEFAULT_FLRA_INCLUDED_COUNT
  }));
}

function renderField(field, container) {
  if (field.type === 'tailgate-items')  { renderTailgateItems(field, container); return; }
  if (field.type === 'flra-table')      { renderFLRATable(field, container); return; }
  if (field.type === 'closeout')        { renderCloseout(field, container); return; }
  if (field.type === 'crew-signin')     { renderCrewSignin(field, container); return; }
  if (field.type === 'signature')       { renderSignaturePad(field, container); return; }
  if (field.type === 'gc-signature')    { renderSignaturePad(field, container, { sigKey: 'gcSignature', padKey: '_gcSignaturePad', canvasId: 'gcSignatureCanvas', disclaimer: 'GC supervisor signs to confirm the labour and materials recorded above are accurate.' }); return; }
  if (field.type === 'timesheet-table')   { renderTimesheetTable(field, container); return; }
  if (field.type === 'production-table')  { renderProductionTable(field, container); return; }
  if (field.type === 'ewo-labour-table')  { renderEwoLabourTable(field, container);  return; }
  if (field.type === 'ewo-materials-table'){ renderEwoMaterialsTable(field, container); return; }
  if (field.type === 'weekly-inspection') { renderWeeklyInspection(field, container); return; }
  if (field.type === 'daily-inspection')  { renderDailyInspection(field, container);  return; }
  if (field.type === 'qaqc-table')        { renderQAQCTable(field, container);         return; }
  if (field.type === 'section') {
    const div = document.createElement('div');
    div.className = 'section-header';
    div.style.marginTop = '20px';
    div.textContent = field.label;
    container.appendChild(div);
    return;
  }
  if (field.type === 'toolbox-hazards') { renderToolboxHazards(field, container); return; }

  const wrapper = document.createElement('div');
  wrapper.className = 'field-group';
  const label = document.createElement('label');
  label.className = 'field-label';
  label.htmlFor = `field_${field.id}`;
  label.textContent = field.label + (field.required ? ' *' : '');
  wrapper.appendChild(label);

  let input;

  if (field.type === 'textarea') {
    // Build textarea + a floating 🎤 mic button (voice-to-text) layered on top.
    // The mic uses the Web Speech API — supported on Chrome/Edge/Safari mobile.
    // Foreman taps the mic, dictates, and the transcript is appended.
    const ta = document.createElement('textarea');
    ta.className = 'field-input field-textarea';
    ta.id = `field_${field.id}`;
    ta.rows = 3;
    ta.placeholder = field.placeholder || '';
    ta.value = state.fields[field.id] || '';
    ta.addEventListener('input', e => { state.fields[field.id] = e.target.value; });

    const taWrap = document.createElement('div');
    taWrap.className = 'textarea-wrap';
    taWrap.appendChild(ta);
    const mic = _buildMicButton(ta, field.id);
    if (mic) taWrap.appendChild(mic);

    wrapper.appendChild(taWrap);
    container.appendChild(wrapper);
    return;

  } else if (field.type === 'checkbox-group') {
    const grid = document.createElement('div');
    grid.className = 'checkbox-grid';
    const selected = state.fields[field.id] || [];
    field.options.forEach(opt => {
      const item = document.createElement('label');
      item.className = 'checkbox-item';
      const cb = document.createElement('input');
      cb.type = 'checkbox'; cb.value = opt; cb.checked = selected.includes(opt);
      cb.addEventListener('change', () => {
        const cur = state.fields[field.id] || [];
        state.fields[field.id] = cb.checked ? [...cur, opt] : cur.filter(v => v !== opt);
      });
      item.appendChild(cb);
      item.appendChild(document.createTextNode(' ' + opt));
      grid.appendChild(item);
    });
    wrapper.appendChild(grid);
    container.appendChild(wrapper);
    return;

  } else if (field.type === 'radio') {
    const group = document.createElement('div');
    group.className = 'radio-group';
    field.options.forEach(opt => {
      const item = document.createElement('label');
      item.className = 'radio-item';
      const rb = document.createElement('input');
      rb.type = 'radio'; rb.name = `field_${field.id}`; rb.value = opt;
      rb.checked = state.fields[field.id] === opt;
      rb.addEventListener('change', () => { state.fields[field.id] = opt; });
      item.appendChild(rb);
      item.appendChild(document.createTextNode(' ' + opt));
      group.appendChild(item);
    });
    wrapper.appendChild(group);
    container.appendChild(wrapper);
    return;

  } else if (field.type === 'select') {
    input = document.createElement('select');
    input.className = 'field-input field-select';
    const blank = document.createElement('option');
    blank.value = ''; blank.textContent = 'Select...';
    input.appendChild(blank);
    field.options.forEach(opt => {
      const o = document.createElement('option');
      o.value = opt; o.textContent = opt;
      if (state.fields[field.id] === opt) o.selected = true;
      input.appendChild(o);
    });
    input.addEventListener('change', e => { state.fields[field.id] = e.target.value; });

  } else {
    input = document.createElement('input');
    input.className = 'field-input';
    input.type = field.type || 'text';
    input.placeholder = field.placeholder || '';
    input.value = state.fields[field.id] || '';
    input.addEventListener('input', e => { state.fields[field.id] = e.target.value; });

    // ── Auto-fill from login / context ────────────────────────────────────────
    if (field.autofill === 'foreman') {
      // Honour the admin "submit on behalf of" picker if set — falls through to
      // the logged-in admin's own name when the override is null.
      const name = _effectiveForemanName() || state.foremanName || '';
      input.value = name;
      state.fields[field.id] = name;
      input.readOnly = true;
      input.classList.add('field-input--prefilled');
    } else if (field.autofill === 'project_code') {
      // Extract TM code — first token before the dash (e.g. "25TM010 - Axiom…" → "25TM010")
      const code = (state.project || '').split(/\s*[-–]\s*/)[0].trim();
      input.value = code;
      state.fields[field.id] = code;
      input.readOnly = true;
      input.classList.add('field-input--prefilled');
    } else if (field.autofill === 'ewo_number') {
      // Auto-fill the next EWO number for the selected project (placeholder
      // "EWO-###" until the RPC returns). The number is also re-checked at
      // submit time so two foremen filing at once don't collide.
      input.value = 'EWO-…';
      input.readOnly = true;
      input.classList.add('field-input--prefilled');
      _peekNextEwoNumber().then(n => {
        if (n != null) {
          const formatted = 'EWO-' + String(n).padStart(3, '0');
          input.value = formatted;
          state.fields[field.id] = formatted;
          state._ewoNextNumber = n;
        }
      });
    }
  }

  if (input) { input.id = `field_${field.id}`; wrapper.appendChild(input); }
  container.appendChild(wrapper);
}

// ── Voice-to-text mic button (Web Speech API) ────────────────────────────────
// Drops a small 🎤 button in the corner of a textarea. Tap to start dictation;
// tap again to stop. The transcript is appended to whatever is already in the
// textarea, so foreman can dictate in chunks. Auto-detects browser language
// (pt-BR for Brazilian foremen, en-US for English) — falls back to en-US.
//
// If the browser doesn't support the API (rare on phones — Chrome/Edge/Safari
// all support it), returns null and the textarea is shown alone.
function _buildMicButton(textarea, fieldId) {
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SR) return null;

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'textarea-mic';
  btn.title = 'Voice to text — tap to speak';
  btn.innerHTML = '🎤';
  btn.setAttribute('aria-label', 'Dictate text into ' + (fieldId || 'this field'));

  let recognition = null;
  let listening = false;

  const start = () => {
    try {
      recognition = new SR();
      recognition.lang = navigator.language || 'en-US';
      recognition.continuous = true;
      recognition.interimResults = false;   // only final results — cleaner
      recognition.maxAlternatives = 1;

      recognition.onresult = (event) => {
        // Append the final transcript to the textarea + sync state
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const r = event.results[i];
          if (!r.isFinal) continue;
          const txt = r[0].transcript.trim();
          if (!txt) continue;
          const sep = textarea.value && !/\s$/.test(textarea.value) ? ' ' : '';
          textarea.value = textarea.value + sep + txt;
          state.fields[fieldId] = textarea.value;
        }
        // Scroll to bottom of textarea so the new text is visible
        textarea.scrollTop = textarea.scrollHeight;
      };

      recognition.onend = () => { stop(); };
      recognition.onerror = (e) => {
        console.warn('speech recognition error:', e.error);
        stop();
        if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
          showToast('Microphone permission denied — enable it in browser settings.', 'warning');
        }
      };

      recognition.start();
      listening = true;
      btn.classList.add('textarea-mic--listening');
      btn.innerHTML = '⏺️';
      btn.title = 'Tap to stop dictation';
    } catch (err) {
      console.warn('SpeechRecognition start failed:', err);
      stop();
    }
  };

  const stop = () => {
    listening = false;
    btn.classList.remove('textarea-mic--listening');
    btn.innerHTML = '🎤';
    btn.title = 'Voice to text — tap to speak';
    if (recognition) { try { recognition.stop(); } catch (_) {} recognition = null; }
  };

  btn.addEventListener('click', (e) => {
    e.preventDefault();
    if (listening) stop(); else start();
  });

  return btn;
}

// ── Tailgate Items Reviewed ───────────────────────────────────────────────────
function renderTailgateItems(field, container) {
  const header = document.createElement('div');
  header.className = 'section-header';
  header.textContent = field.label;
  container.appendChild(header);

  const body = document.createElement('div');
  body.className = 'section-body';

  if (!state.fields[field.id]) state.fields[field.id] = {};

  Object.entries(TAILGATE_ITEMS).forEach(([section, items]) => {
    const secTitle = document.createElement('div');
    secTitle.className = 'tailgate-section-title';
    secTitle.textContent = section;
    body.appendChild(secTitle);

    const list = document.createElement('div');
    list.className = 'tailgate-checkbox-list';

    items.forEach(item => {
      const lbl = document.createElement('label');
      lbl.className = 'tailgate-checkbox-item';
      const cb = document.createElement('input');
      cb.type = 'checkbox';
      cb.checked = !!(state.fields[field.id][item]);
      cb.addEventListener('change', () => {
        state.fields[field.id][item] = cb.checked;
      });
      lbl.appendChild(cb);
      lbl.appendChild(document.createTextNode(' ' + item));
      list.appendChild(lbl);
    });

    body.appendChild(list);
  });

  container.appendChild(body);
}

// ── FLRA Table ────────────────────────────────────────────────────────────────
function renderFLRATable(field, container) {
  const header = document.createElement('div');
  header.className = 'section-header';
  header.textContent = field.label;
  container.appendChild(header);

  const body = document.createElement('div');
  body.className = 'section-body';

  // Initialize: presets start unchecked — foreman selects what applies today
  if (!state.fields[field.id] || state.fields[field.id].length === 0) {
    state.fields[field.id] = DEFAULT_FLRA_TASKS.map(t => ({
      ...t, hazards: [...t.hazards], included: false
    }));
  }

  const presetCount = DEFAULT_FLRA_TASKS.length;

  // ── Preset task selector ──
  const presetLabel = document.createElement('p');
  presetLabel.className = 'flra-section-label';
  presetLabel.textContent = 'Tap to select tasks for today:';
  body.appendChild(presetLabel);

  const presetList = document.createElement('div');
  presetList.className = 'flra-preset-list';
  presetList.id = 'flraPresetList';
  body.appendChild(presetList);

  for (let i = 0; i < presetCount; i++) {
    renderFLRAPresetItem(presetList, i);
  }

  // ── Custom tasks ──
  const customLabel = document.createElement('p');
  customLabel.className = 'flra-section-label';
  customLabel.style.marginTop = '16px';
  customLabel.textContent = 'Additional tasks (if needed):';
  body.appendChild(customLabel);

  const customRowsDiv = document.createElement('div');
  customRowsDiv.id = 'flraRows';
  body.appendChild(customRowsDiv);

  for (let i = presetCount; i < state.fields[field.id].length; i++) {
    renderFLRARow(customRowsDiv, i);
  }

  const addBtn = document.createElement('button');
  addBtn.type = 'button';
  addBtn.className = 'add-flra-btn';
  addBtn.textContent = '+ Add Custom Task';
  addBtn.addEventListener('click', () => {
    state.fields[field.id].push({ task: '', hazards: [], riskLevel: 'Low', controls: '', included: true });
    renderFLRARow(customRowsDiv, state.fields[field.id].length - 1);
  });
  body.appendChild(addBtn);
  container.appendChild(body);
}

// ── Compact preset item (tap to include) ─────────────────────────────────────
function renderFLRAPresetItem(container, index) {
  const row = state.fields.flra[index];
  if (row.included === undefined) row.included = false;

  const item = document.createElement('div');
  item.className = 'flra-preset-item' + (row.included ? ' included' : '');
  item.id = `flraPreset${index}`;

  // Header: checkbox + task name + risk pill
  const headerLabel = document.createElement('label');
  headerLabel.className = 'flra-preset-header';

  const cb = document.createElement('input');
  cb.type = 'checkbox';
  cb.className = 'flra-preset-cb';
  cb.checked = !!row.included;

  const nameSpan = document.createElement('span');
  nameSpan.className = 'flra-preset-name';
  nameSpan.textContent = row.task;

  const riskPill = document.createElement('span');
  riskPill.className = `flra-risk-pill ${(row.riskLevel || 'low').toLowerCase()}`;
  riskPill.textContent = row.riskLevel || 'Low';

  headerLabel.appendChild(cb);
  headerLabel.appendChild(nameSpan);
  headerLabel.appendChild(riskPill);
  item.appendChild(headerLabel);

  // Detail block — visible only when included
  const detail = document.createElement('div');
  detail.className = 'flra-preset-detail';

  if (row.hazards && row.hazards.length > 0) {
    const chips = document.createElement('div');
    chips.className = 'flra-hazard-chips';
    row.hazards.forEach(h => {
      const chip = document.createElement('span');
      chip.className = 'flra-hazard-chip';
      chip.textContent = h;
      chips.appendChild(chip);
    });
    detail.appendChild(chips);
  }

  if (row.controls) {
    const ctrl = document.createElement('div');
    ctrl.className = 'flra-preset-ctrl-text';
    ctrl.textContent = row.controls;
    detail.appendChild(ctrl);
  }

  item.appendChild(detail);
  container.appendChild(item);

  cb.addEventListener('change', () => {
    state.fields.flra[index].included = cb.checked;
    item.classList.toggle('included', cb.checked);
  });
}

// ── Custom task row (full form) ───────────────────────────────────────────────
function renderFLRARow(container, index) {
  const row = state.fields.flra[index];
  const customNum = index - DEFAULT_FLRA_TASKS.length + 1;

  const div = document.createElement('div');
  div.className = 'flra-row';
  div.id = `flraRow${index}`;

  div.innerHTML = `
    <div class="flra-row-header">
      <span class="flra-row-num">Custom Task ${customNum}</span>
      <button type="button" class="flra-remove" data-idx="${index}">×</button>
    </div>
    <div class="field-group" style="margin-bottom:12px">
      <label class="field-label">Task Description</label>
      <input class="field-input" type="text" placeholder="Describe the work task..." value="${row.task || ''}" data-flra-task="${index}">
    </div>
    <div class="field-group" style="margin-bottom:12px">
      <label class="field-label">Hazards Present</label>
      <div class="checkbox-grid" id="flraHazards${index}"></div>
    </div>
    <div class="field-group" style="margin-bottom:12px">
      <label class="field-label">Hazard Level</label>
      <div class="flra-risk-btns">
        <button type="button" class="risk-btn ${row.riskLevel === 'High' ? 'high' : ''}" data-risk="High" data-flra-risk="${index}">High</button>
        <button type="button" class="risk-btn ${row.riskLevel === 'Medium' ? 'medium' : ''}" data-risk="Medium" data-flra-risk="${index}">Medium</button>
        <button type="button" class="risk-btn ${row.riskLevel === 'Low' ? 'low' : ''}" data-risk="Low" data-flra-risk="${index}">Low</button>
      </div>
    </div>
    <div class="field-group" style="margin-bottom:0">
      <label class="field-label">Controls in Place</label>
      <textarea class="field-input field-textarea" rows="2" placeholder="Controls applied to reduce risk..." data-flra-controls="${index}">${row.controls || ''}</textarea>
    </div>`;

  container.appendChild(div);

  // Hazard checkboxes
  const hazardGrid = div.querySelector(`#flraHazards${index}`);
  FLRA_HAZARDS.forEach(h => {
    const lbl = document.createElement('label');
    lbl.className = 'checkbox-item';
    const hcb = document.createElement('input');
    hcb.type = 'checkbox';
    hcb.checked = (row.hazards || []).includes(h);
    hcb.addEventListener('change', () => {
      const cur = state.fields.flra[index].hazards || [];
      state.fields.flra[index].hazards = hcb.checked ? [...cur, h] : cur.filter(v => v !== h);
    });
    lbl.appendChild(hcb);
    lbl.appendChild(document.createTextNode(' ' + h));
    hazardGrid.appendChild(lbl);
  });

  div.querySelector(`[data-flra-task="${index}"]`).addEventListener('input', e => {
    state.fields.flra[index].task = e.target.value;
  });

  div.querySelector(`[data-flra-controls="${index}"]`).addEventListener('input', e => {
    state.fields.flra[index].controls = e.target.value;
  });

  div.querySelectorAll(`[data-flra-risk="${index}"]`).forEach(btn => {
    btn.addEventListener('click', () => {
      div.querySelectorAll(`[data-flra-risk="${index}"]`).forEach(b => b.className = 'risk-btn');
      btn.classList.add(btn.dataset.risk.toLowerCase());
      state.fields.flra[index].riskLevel = btn.dataset.risk;
    });
  });

  // Remove button — only re-renders custom rows
  const removeBtn = div.querySelector(`[data-idx="${index}"]`);
  if (removeBtn) {
    removeBtn.addEventListener('click', () => {
      state.fields.flra.splice(index, 1);
      const rowsContainer = document.getElementById('flraRows');
      rowsContainer.innerHTML = '';
      const presetCount = DEFAULT_FLRA_TASKS.length;
      for (let i = presetCount; i < state.fields.flra.length; i++) {
        renderFLRARow(rowsContainer, i);
      }
    });
  }
}

// ── Close Out ─────────────────────────────────────────────────────────────────
function renderCloseout(field, container) {
  const header = document.createElement('div');
  header.className = 'section-header';
  header.textContent = field.label;
  container.appendChild(header);

  const body = document.createElement('div');
  body.className = 'section-body';

  if (!state.fields[field.id]) state.fields[field.id] = {};

  CLOSEOUT_QUESTIONS.forEach(q => {
    const row = document.createElement('div');
    row.className = 'closeout-row';

    const qText = document.createElement('span');
    qText.className = 'closeout-question';
    qText.textContent = q;

    const btns = document.createElement('div');
    btns.className = 'closeout-btns';

    const yesBtn = document.createElement('button');
    yesBtn.type = 'button';
    yesBtn.className = 'yn-btn' + (state.fields[field.id][q] === 'Yes' ? ' selected-yes' : '');
    yesBtn.textContent = 'Yes';

    const noBtn = document.createElement('button');
    noBtn.type = 'button';
    noBtn.className = 'yn-btn' + (state.fields[field.id][q] === 'No' ? ' selected-no' : '');
    noBtn.textContent = 'No';

    yesBtn.addEventListener('click', () => {
      state.fields[field.id][q] = 'Yes';
      yesBtn.className = 'yn-btn selected-yes';
      noBtn.className = 'yn-btn';
    });
    noBtn.addEventListener('click', () => {
      state.fields[field.id][q] = 'No';
      noBtn.className = 'yn-btn selected-no';
      yesBtn.className = 'yn-btn';
    });

    btns.appendChild(yesBtn);
    btns.appendChild(noBtn);
    row.appendChild(qText);
    row.appendChild(btns);
    body.appendChild(row);
  });

  container.appendChild(body);
}

// ── Toolbox Hazard Review ─────────────────────────────────────────────────────
function renderToolboxHazards(field, container) {
  if (!state.fields.hazard_review) {
    state.fields.hazard_review = TOOLBOX_HAZARDS.map(h => ({ hazard: h, risk: '', control: '', included: false }));
    // add one blank custom row
    state.fields.hazard_review.push({ hazard: '', risk: '', control: '', included: false, custom: true });
  }
  const rows = state.fields.hazard_review;

  const wrap = document.createElement('div');
  wrap.style.cssText = 'display:flex;flex-direction:column;gap:8px;';

  function rebuild() { wrap.innerHTML = ''; buildRows(); }

  function buildRows() {
    rows.forEach((row, i) => {
      const card = document.createElement('div');
      card.className = `flra-preset-item${row.included ? ' included' : ''}`;

      // Header row: checkbox + hazard name + risk pill
      const hdr = document.createElement('div');
      hdr.className = 'flra-preset-header';
      hdr.style.gap = '10px';

      const cb = document.createElement('input');
      cb.type = 'checkbox'; cb.className = 'flra-preset-cb';
      cb.checked = !!row.included;
      cb.addEventListener('change', () => { rows[i].included = cb.checked; rebuild(); });

      let nameEl;
      if (row.custom) {
        nameEl = document.createElement('input');
        nameEl.type = 'text'; nameEl.className = 'field-input';
        nameEl.style.cssText = 'flex:1;padding:6px 10px;font-size:13px;';
        nameEl.placeholder = 'Other hazard / task...';
        nameEl.value = row.hazard || '';
        nameEl.addEventListener('input', e => { rows[i].hazard = e.target.value; });
      } else {
        nameEl = document.createElement('span');
        nameEl.className = 'flra-preset-name';
        nameEl.textContent = row.hazard;
      }

      // Risk buttons H / M / L
      const riskWrap = document.createElement('div');
      riskWrap.style.cssText = 'display:flex;gap:4px;flex-shrink:0;';
      ['H', 'M', 'L'].forEach(level => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.textContent = level;
        const cls = level === 'H' ? 'high' : level === 'M' ? 'medium' : 'low';
        btn.className = `risk-btn${row.risk === level ? ' ' + cls : ''}`;
        btn.style.cssText = 'flex:none;width:32px;padding:5px 0;font-size:11px;';
        btn.addEventListener('click', () => { rows[i].risk = level; rebuild(); });
        riskWrap.appendChild(btn);
      });

      hdr.append(cb, nameEl, riskWrap);
      card.appendChild(hdr);

      // Detail block (controls) — visible when included
      if (row.included) {
        const detail = document.createElement('div');
        detail.className = 'flra-preset-detail';
        detail.style.display = 'block';
        const ctrl = document.createElement('textarea');
        ctrl.className = 'field-input field-textarea';
        ctrl.style.cssText = 'min-height:56px;font-size:13px;';
        ctrl.placeholder = 'Control / action to mitigate this hazard...';
        ctrl.value = row.control || '';
        ctrl.addEventListener('input', e => { rows[i].control = e.target.value; });
        detail.appendChild(ctrl);
        card.appendChild(detail);
      }

      wrap.appendChild(card);
    });
  }

  buildRows();
  container.appendChild(wrap);
}

// ── Crew Sign-In ──────────────────────────────────────────────────────────────
function renderCrewSignin(field, container) {
  const header = document.createElement('div');
  header.className = 'section-header';
  header.textContent = field.label;
  container.appendChild(header);

  const body = document.createElement('div');
  body.className = 'section-body';

  const rowsWrap = document.createElement('div');
  rowsWrap.id = 'crewRows';
  body.appendChild(rowsWrap);

  const addBtn = document.createElement('button');
  addBtn.type = 'button';
  addBtn.className = 'add-crew-btn';
  addBtn.textContent = '+ Add Worker';
  addBtn.addEventListener('click', () => {
    state.fields[field.id].push({ name: '', sig: null });
    renderCrewRow(rowsWrap, state.fields[field.id].length - 1);
  });
  body.appendChild(addBtn);
  container.appendChild(body);

  // Load roster asynchronously — renders once data arrives (non-blocking)
  _loadCrewRoster(field.id, rowsWrap);
}

async function _loadCrewRoster(fieldId, rowsWrap) {
  // Only pre-fill from DB if the list is empty (don't override existing/draft data).
  // Roster is the foreman's own assigned workers (worker_assignments) — NOT the
  // project crew. Inactive workers are filtered out. The foreman can still delete
  // any name they don't need, or add extras with "+ Add Worker".
  if (!state.fields[fieldId] || state.fields[fieldId].length === 0) {
    try {
      const foremanId = window.currentUser?.id;
      if (foremanId) {
        // Pull worker_ids assigned to this foreman, then join workers for the name + active flag
        const { data: rows } = await sbClient
          .from('worker_assignments')
          .select('workers(name, active)')
          .eq('foreman_id', foremanId);

        const names = (rows || [])
          .map(r => r.workers)
          .filter(w => w && w.active && w.name)
          .map(w => w.name.trim())
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b));

        if (names.length) {
          state.fields[fieldId] = names.map(name => ({ name, sig: null }));
        }
      }
    } catch (_) {}
    if (!state.fields[fieldId]?.length) {
      state.fields[fieldId] = [{ name: '', sig: null }];
    }
  }

  // Render all rows now that data is ready
  rowsWrap.innerHTML = '';
  state.fields[fieldId].forEach((_, i) => renderCrewRow(rowsWrap, i));
}

function renderCrewRow(container, index) {
  const member = state.fields.crew[index];
  const card = document.createElement('div');
  card.className = 'crew-card';

  // ── Name row ─────────────────────────────────────────────
  const nameRow = document.createElement('div');
  nameRow.className = 'crew-card-header';

  const num = document.createElement('span');
  num.className = 'crew-num';
  num.textContent = index + 1;

  const nameInput = document.createElement('input');
  nameInput.className = 'field-input crew-name';
  nameInput.type = 'text';
  nameInput.placeholder = 'Full name';
  nameInput.value = member.name || '';
  nameInput.setAttribute('list', 'workersDatalist');   // crew autofill from workers master list
  nameInput.setAttribute('autocomplete', 'off');       // suppress browser's own history suggestions
  nameInput.addEventListener('input', e => { state.fields.crew[index].name = e.target.value; });

  const removeBtn = document.createElement('button');
  removeBtn.type = 'button';
  removeBtn.className = 'crew-remove';
  removeBtn.textContent = '×';
  removeBtn.addEventListener('click', () => {
    if (state.fields.crew.length <= 1) return;
    state.fields.crew.splice(index, 1);
    const wrap = document.getElementById('crewRows');
    wrap.innerHTML = '';
    state.fields.crew.forEach((_, i) => renderCrewRow(wrap, i));
  });

  nameRow.append(num, nameInput, removeBtn);
  card.appendChild(nameRow);

  // ── Signature area ────────────────────────────────────────
  const sigLabel = document.createElement('div');
  sigLabel.className = 'crew-sig-label';
  sigLabel.textContent = 'Signature';
  card.appendChild(sigLabel);

  const sigWrap = document.createElement('div');
  sigWrap.className = 'crew-sig-wrap';

  const canvas = document.createElement('canvas');
  canvas.className = 'crew-sig-canvas';
  // Set pixel resolution after mount (rAF gives layout time to settle)
  sigWrap.appendChild(canvas);

  const sigActions = document.createElement('div');
  sigActions.className = 'crew-sig-actions';

  const hint = document.createElement('span');
  hint.className = 'crew-sig-hint';
  hint.textContent = 'Sign with finger';

  const clearBtn = document.createElement('button');
  clearBtn.type = 'button';
  clearBtn.className = 'crew-sig-clear';
  clearBtn.textContent = 'Clear';

  sigActions.append(hint, clearBtn);
  sigWrap.appendChild(sigActions);
  card.appendChild(sigWrap);
  container.appendChild(card);

  // Init SignaturePad after the canvas is in the DOM
  requestAnimationFrame(() => {
    const ratio = window.devicePixelRatio || 1;
    canvas.width  = canvas.offsetWidth  * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    const ctx = canvas.getContext('2d');
    ctx.scale(ratio, ratio);

    const pad = new SignaturePad(canvas, {
      penColor: '#1a1a2e',
      backgroundColor: 'rgba(255,255,255,0)',
      minWidth: 1,
      maxWidth: 2.5,
    });

    // Restore saved signature
    if (member.sig) {
      pad.fromDataURL(member.sig, { ratio: 1, width: canvas.offsetWidth, height: canvas.offsetHeight });
    }

    pad.addEventListener('endStroke', () => {
      state.fields.crew[index].sig = pad.toDataURL('image/png');
    });

    clearBtn.addEventListener('click', () => {
      pad.clear();
      state.fields.crew[index].sig = null;
    });
  });
}

// ── Signature Pad ─────────────────────────────────────────────────────────────
// Signature pad renderer. Default config writes to state.signature (the
// foreman's signature, used by every safety form). For Extra Work Order's
// GC supervisor pad, pass { sigKey: 'gcSignature', padKey: '_gcSignaturePad' }
// so the two pads coexist without stomping each other's state.
function renderSignaturePad(field, container, opts = {}) {
  const sigKey   = opts.sigKey   || 'signature';
  const padKey   = opts.padKey   || '_signaturePad';
  const canvasId = opts.canvasId || 'signatureCanvas';
  const isOptional = opts.optional ?? (sigKey === 'gcSignature');   // GC sig is optional
  const disclaimer = opts.disclaimer
    ?? 'I certify that I have reviewed this questionnaire with each crew member and am complying with the guidelines outlined by Trade Mark and the Ministry of Health.';

  const wrapper = document.createElement('div');
  wrapper.className = 'field-group';

  const label = document.createElement('label');
  label.className = 'field-label';
  label.textContent = field.label + (isOptional ? ' (optional)' : ' *');
  wrapper.appendChild(label);

  const disclaimerEl = document.createElement('p');
  disclaimerEl.className = 'signature-disclaimer';
  disclaimerEl.textContent = disclaimer;
  wrapper.appendChild(disclaimerEl);

  const wrap = document.createElement('div');
  wrap.className = 'signature-wrap';

  const canvas = document.createElement('canvas');
  canvas.className = 'signature-canvas';
  canvas.id = canvasId;

  const actions = document.createElement('div');
  actions.className = 'signature-actions';
  actions.innerHTML = '<span class="signature-hint">Sign with your finger</span>';

  const clearBtn = document.createElement('button');
  clearBtn.type = 'button';
  clearBtn.className = 'signature-clear-btn';
  clearBtn.textContent = 'Clear';
  actions.appendChild(clearBtn);

  wrap.appendChild(canvas);
  wrap.appendChild(actions);
  wrapper.appendChild(wrap);
  container.appendChild(wrapper);

  // Init after element is in DOM
  setTimeout(() => {
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const canvasW = canvas.offsetWidth || canvas.parentElement?.offsetWidth || 320;
    canvas.width = canvasW * ratio;
    canvas.height = 180 * ratio;
    canvas.style.height = '180px';
    canvas.getContext('2d').scale(ratio, ratio);

    if (!window.SignaturePad) {
      canvas.parentElement.insertAdjacentHTML('afterend', '<p style="color:#ef4444;font-size:12px;margin-top:6px">Signature pad failed to load. Check internet connection.</p>');
      return;
    }

    const pad = new SignaturePad(canvas, {
      backgroundColor: 'rgb(255,255,255)',
      penColor: '#1E3A6E',
      minWidth: 1.5,
      maxWidth: 3
    });

    if (state[sigKey]) pad.fromDataURL(state[sigKey]);

    pad.addEventListener('endStroke', () => { state[sigKey] = pad.toDataURL('image/png'); });
    clearBtn.addEventListener('click', () => { pad.clear(); state[sigKey] = null; });
    window[padKey] = pad;
  }, 150);
}

// ── Timesheet Table ───────────────────────────────────────────────────────────
const TS_DAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

function emptyTsEmployee() {
  const days = {};
  TS_DAYS.forEach(d => { days[d.toLowerCase()] = { r: '', ot: '', sick: false }; });
  return { name: '', worker_id: '', days, notes: '' };
}

function renderTimesheetTable(field, container) {
  const header = document.createElement('div');
  header.className = 'section-header';
  header.textContent = field.label;
  container.appendChild(header);

  const body = document.createElement('div');
  body.className = 'section-body';

  if (!state.fields[field.id] || state.fields[field.id].length === 0) {
    state.fields[field.id] = [emptyTsEmployee()];
  }

  const rowsWrap = document.createElement('div');
  rowsWrap.id = 'tsRows';
  body.appendChild(rowsWrap);
  state.fields[field.id].forEach((_, i) => renderTsEmployee(rowsWrap, i));

  const addBtn = document.createElement('button');
  addBtn.type = 'button';
  addBtn.className = 'add-crew-btn';
  addBtn.textContent = '+ Add Employee';
  addBtn.addEventListener('click', () => {
    state.fields.timesheet.push(emptyTsEmployee());
    renderTsEmployee(rowsWrap, state.fields.timesheet.length - 1);
  });
  body.appendChild(addBtn);
  container.appendChild(body);
}

function renderTsEmployee(container, index) {
  const emp = state.fields.timesheet[index];
  const card = document.createElement('div');
  card.className = 'ts-employee-card';
  card.id = `tsEmp${index}`;

  // ── Name row ──
  const nameRow = document.createElement('div');
  nameRow.className = 'ts-name-row';

  // Worker picker — dropdown only, no freehand text. This forces every entry
  // to map to a real row in the workers table so syncTimesheetToDb can never
  // silently skip an unrecognised name. Pre-loaded into window.workersFull.
  const nameInput = document.createElement('select');
  nameInput.className = 'field-input ts-name-input ts-name-select';
  const placeholder = document.createElement('option');
  placeholder.value = '';
  placeholder.textContent = `— Select employee ${index + 1} —`;
  placeholder.disabled = true;
  placeholder.selected = !emp.worker_id && !emp.name;
  nameInput.appendChild(placeholder);

  const workers = Array.isArray(window.workersFull) ? window.workersFull : [];
  if (!workers.length) {
    const opt = document.createElement('option');
    opt.value = ''; opt.disabled = true;
    opt.textContent = 'Loading workers… reopen this form if list stays empty';
    nameInput.appendChild(opt);
  } else {
    workers.forEach(w => {
      const o = document.createElement('option');
      o.value = w.id;
      o.dataset.name  = w.name || '';
      o.dataset.trade = w.trade || '';
      o.textContent = w.trade ? `${w.name} — ${w.trade}` : w.name;
      // Restore prior selection by id first (most accurate), then by name (legacy drafts)
      if ((emp.worker_id && emp.worker_id === w.id) ||
          (!emp.worker_id && emp.name && emp.name.toLowerCase() === (w.name || '').toLowerCase())) {
        o.selected = true;
      }
      nameInput.appendChild(o);
    });
  }
  nameInput.addEventListener('change', e => {
    const opt = e.target.selectedOptions[0];
    state.fields.timesheet[index].worker_id = e.target.value || '';
    state.fields.timesheet[index].name      = opt?.dataset.name || '';
    updateTsTotals(card, index);
  });

  const removeBtn = document.createElement('button');
  removeBtn.type = 'button';
  removeBtn.className = 'crew-remove';
  removeBtn.textContent = '×';
  removeBtn.addEventListener('click', () => {
    if (state.fields.timesheet.length <= 1) return;
    state.fields.timesheet.splice(index, 1);
    const wrap = document.getElementById('tsRows');
    wrap.innerHTML = '';
    state.fields.timesheet.forEach((_, i) => renderTsEmployee(wrap, i));
  });

  nameRow.appendChild(nameInput);
  nameRow.appendChild(removeBtn);
  card.appendChild(nameRow);

  // ── Days grid — 4 cols first row, 3 cols second row ──
  const grid = document.createElement('div');
  grid.className = 'ts-days-grid';

  TS_DAYS.forEach(day => {
    const dayCol = document.createElement('div');
    dayCol.className = 'ts-day-col';

    const dayKey = day.toLowerCase();
    const dayLabel = document.createElement('div');
    dayLabel.className = 'ts-day-label';
    dayLabel.textContent = day.toUpperCase();

    const regInput = document.createElement('input');
    regInput.className = 'field-input ts-hours-input';
    regInput.type = 'number';
    regInput.min = '0'; regInput.max = '24'; regInput.step = '0.5';
    regInput.placeholder = 'R';
    regInput.value = emp.days[dayKey]?.r || '';
    regInput.title = `${day} Regular`;
    regInput.addEventListener('input', e => {
      state.fields.timesheet[index].days[dayKey].r = e.target.value;
      updateTsTotals(card, index);
    });

    const otInput = document.createElement('input');
    otInput.className = 'field-input ts-hours-input ts-ot-input';
    otInput.type = 'number';
    otInput.min = '0'; otInput.max = '24'; otInput.step = '0.5';
    otInput.placeholder = 'OT';
    otInput.value = emp.days[dayKey]?.ot || '';
    otInput.title = `${day} Overtime`;
    otInput.addEventListener('input', e => {
      state.fields.timesheet[index].days[dayKey].ot = e.target.value;
      updateTsTotals(card, index);
    });

    // SICK toggle — marks the whole day as a sick day. Clears/locks the hour
    // inputs; on submit it lands in the sick_days table (admin Sick Days tab).
    const sickBtn = document.createElement('button');
    sickBtn.type = 'button';
    sickBtn.className = 'ts-sick-btn';
    sickBtn.textContent = 'SICK';
    sickBtn.title = `${day} — mark as sick day`;
    const applySick = (on) => {
      regInput.disabled = on;
      otInput.disabled = on;
      sickBtn.classList.toggle('active', on);
    };
    applySick(!!emp.days[dayKey]?.sick);
    sickBtn.addEventListener('click', () => {
      const d = state.fields.timesheet[index].days[dayKey];
      d.sick = !d.sick;
      if (d.sick) { d.r = ''; d.ot = ''; regInput.value = ''; otInput.value = ''; }
      applySick(d.sick);
      updateTsTotals(card, index);
    });

    dayCol.appendChild(dayLabel);
    dayCol.appendChild(regInput);
    dayCol.appendChild(otInput);
    dayCol.appendChild(sickBtn);
    grid.appendChild(dayCol);
  });

  card.appendChild(grid);

  // ── Totals bar ──
  const totalsBar = document.createElement('div');
  totalsBar.className = 'ts-totals-bar';
  totalsBar.id = `tsTotals${index}`;
  card.appendChild(totalsBar);
  container.appendChild(card);
  updateTsTotals(card, index);
}

function updateTsTotals(card, index) {
  const emp = state.fields.timesheet[index];
  if (!emp) return;
  let totalR = 0, totalOT = 0, sickCt = 0;
  TS_DAYS.forEach(d => {
    const cell = emp.days[d.toLowerCase()];
    totalR  += parseFloat(cell.r  || 0);
    totalOT += parseFloat(cell.ot || 0);
    if (cell.sick) sickCt++;
  });
  const totalAll = totalR + totalOT;
  const bar = card.querySelector(`#tsTotals${index}`);
  if (bar) {
    bar.innerHTML = `
      <span class="ts-total-chip reg">REG <strong>${totalR % 1 === 0 ? totalR : totalR.toFixed(1)}</strong></span>
      <span class="ts-total-chip ot">O/T <strong>${totalOT % 1 === 0 ? totalOT : totalOT.toFixed(1)}</strong></span>
      <span class="ts-total-chip total">TOTAL <strong>${totalAll % 1 === 0 ? totalAll : totalAll.toFixed(1)}</strong></span>${sickCt ? `
      <span class="ts-total-chip sick">SICK <strong>${sickCt}</strong></span>` : ''}`;
  }
}

// ── Weekly Inspection Checklist ───────────────────────────────────────────────
function renderWeeklyInspection(field, container) {
  const config = INSPECTION_CONFIGS[state.submissionType];
  if (!config) return;

  // Initialise state: each item gets 6 slots [null|'ok'|'x'] for MON–SAT
  if (!state.fields[field.id]) {
    const s = {};
    Object.entries(config.sections).forEach(([sec, items]) => {
      s[sec] = {};
      items.forEach(item => { s[sec][item] = new Array(6).fill(null); });
    });
    state.fields[field.id] = s;
  }

  // Legend notice
  const notice = document.createElement('div');
  notice.className = 'insp-notice';
  notice.innerHTML = '<strong>Tap each day to cycle:</strong>&nbsp; <span class="insp-ok-eg">✓</span> OK &nbsp;·&nbsp; <span class="insp-x-eg">✗</span> Fix needed &nbsp;·&nbsp; tap again to clear';
  container.appendChild(notice);

  Object.entries(config.sections).forEach(([secName, items]) => {
    // Section header
    const secHdr = document.createElement('div');
    secHdr.className = 'insp-section-header';
    secHdr.textContent = secName;
    container.appendChild(secHdr);

    const secBody = document.createElement('div');
    secBody.className = 'insp-section-body';

    // Day labels row
    const dayHdrRow = document.createElement('div');
    dayHdrRow.className = 'insp-day-hdr-row';
    const spacer = document.createElement('div');
    spacer.className = 'insp-item-spacer';
    dayHdrRow.appendChild(spacer);
    const dayHdrs = document.createElement('div');
    dayHdrs.className = 'insp-day-cells';
    INSP_DAYS.forEach(d => {
      const lbl = document.createElement('div');
      lbl.className = 'insp-day-lbl';
      lbl.textContent = d.substring(0, 3);
      dayHdrs.appendChild(lbl);
    });
    dayHdrRow.appendChild(dayHdrs);
    secBody.appendChild(dayHdrRow);

    items.forEach((item, itemIdx) => {
      const row = document.createElement('div');
      row.className = 'insp-item-row' + (itemIdx % 2 === 0 ? '' : ' insp-row-alt');

      const textDiv = document.createElement('div');
      textDiv.className = 'insp-item-text';
      textDiv.textContent = item;

      const cellsDiv = document.createElement('div');
      cellsDiv.className = 'insp-day-cells';

      INSP_DAYS.forEach((_, di) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        const val = state.fields[field.id][secName][item][di];
        btn.className = 'insp-toggle' + (val === 'ok' ? ' ok' : val === 'x' ? ' x' : '');
        btn.textContent = val === 'ok' ? '✓' : val === 'x' ? '✗' : '';
        btn.setAttribute('aria-label', `${INSP_DAYS[di]}: ${val || 'not checked'}`);

        btn.addEventListener('click', () => {
          const cur = state.fields[field.id][secName][item][di];
          const next = cur === null ? 'ok' : cur === 'ok' ? 'x' : null;
          state.fields[field.id][secName][item][di] = next;
          btn.className = 'insp-toggle' + (next === 'ok' ? ' ok' : next === 'x' ? ' x' : '');
          btn.textContent = next === 'ok' ? '✓' : next === 'x' ? '✗' : '';
        });

        cellsDiv.appendChild(btn);
      });

      row.appendChild(textDiv);
      row.appendChild(cellsDiv);
      secBody.appendChild(row);
    });

    container.appendChild(secBody);
  });
}

// ── Daily Inspection Checklist (single date — no day columns) ────────────────
function renderDailyInspection(field, container) {
  const config = INSPECTION_CONFIGS[state.submissionType];
  if (!config) return;

  // Initialise state: each item gets a single value (null | 'ok' | 'x')
  // Always rebuild if structure doesn't match this form's sections (guards against stale state)
  const expectedSecs = Object.keys(config.sections);
  const existing = state.fields[field.id];
  if (!existing || !expectedSecs.every(k => k in existing)) {
    const s = {};
    Object.entries(config.sections).forEach(([sec, items]) => {
      s[sec] = {};
      items.forEach(item => { s[sec][item] = null; });
    });
    state.fields[field.id] = s;
  }

  // Legend notice
  const notice = document.createElement('div');
  notice.className = 'insp-notice';
  notice.innerHTML = '<strong>Tap to cycle:</strong>&nbsp; <span class="insp-ok-eg">✓</span> OK &nbsp;·&nbsp; <span class="insp-x-eg">✗</span> Fix needed &nbsp;·&nbsp; tap again to clear';
  container.appendChild(notice);

  Object.entries(config.sections).forEach(([secName, items]) => {
    const secHdr = document.createElement('div');
    secHdr.className = 'insp-section-header';
    secHdr.textContent = secName;
    container.appendChild(secHdr);

    const secBody = document.createElement('div');
    secBody.className = 'insp-section-body dinsp-body';

    items.forEach((item, itemIdx) => {
      const row = document.createElement('div');
      row.className = 'dinsp-item-row' + (itemIdx % 2 === 0 ? '' : ' insp-row-alt');

      const textDiv = document.createElement('div');
      textDiv.className = 'insp-item-text';
      textDiv.textContent = item;

      const val = (state.fields[field.id][secName] || {})[item] || null;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'insp-toggle dinsp-toggle' + (val === 'ok' ? ' ok' : val === 'x' ? ' x' : '');
      btn.textContent = val === 'ok' ? '✓' : val === 'x' ? '✗' : '';
      btn.setAttribute('aria-label', item + ': ' + (val || 'not checked'));

      btn.addEventListener('click', () => {
        const cur = state.fields[field.id][secName][item];
        const next = cur === null ? 'ok' : cur === 'ok' ? 'x' : null;
        state.fields[field.id][secName][item] = next;
        btn.className = 'insp-toggle dinsp-toggle' + (next === 'ok' ? ' ok' : next === 'x' ? ' x' : '');
        btn.textContent = next === 'ok' ? '✓' : next === 'x' ? '✗' : '';
      });

      row.appendChild(textDiv);
      row.appendChild(btn);
      secBody.appendChild(row);
    });

    container.appendChild(secBody);
  });
}

// ── QAQC Table ────────────────────────────────────────────────────────────────
function renderQAQCTable(field, container) {
  // Item list: staged Field QAQC tables carry a stageKey into FIELD_QAQC_STAGES;
  // the original QAQC form falls back to the classic QAQC_ITEMS list.
  const _stage = field.stageKey && typeof FIELD_QAQC_STAGES !== 'undefined'
    ? FIELD_QAQC_STAGES.find(s => s.key === field.stageKey) : null;
  const ITEMS = _stage ? _stage.items : QAQC_ITEMS;

  // Initialise state: array of { result: null|'pass'|'fail'|'na', comment: '' }
  // Also reinitialise if stale state from another form type is sitting there
  if (!Array.isArray(state.fields[field.id]) || state.fields[field.id].length !== ITEMS.length) {
    state.fields[field.id] = ITEMS.map(() => ({ result: null, comment: '' }));
  }

  // The walkthrough notice only makes sense once — show it on the classic
  // single-table form and on the first stage of the staged (ITP) form.
  if (!field.stageKey || field.stageKey === 'checklist_pre') {
    const notice = document.createElement('div');
    notice.className = 'qaqc-notice';
    notice.innerHTML = 'Inspect each item during the walkthrough. Mark <strong>PASS</strong>, <strong>FAIL</strong>, or <strong>N/A</strong>. A FAIL needs a comment describing the deficiency.';
    container.appendChild(notice);
  }

  // Toolbar: bulk actions + live progress. On a typical all-good walkthrough the
  // foreman taps "All PASS" once instead of 15 times, then overrides exceptions.
  const bar = document.createElement('div');
  bar.className = 'qaqc-toolbar';
  const allBtn = document.createElement('button');
  allBtn.type = 'button';
  allBtn.textContent = '✓ Mark all PASS';
  allBtn.className = 'qaqc-tool-btn qaqc-tool-btn--pass';
  const clrBtn = document.createElement('button');
  clrBtn.type = 'button';
  clrBtn.textContent = 'Clear';
  clrBtn.className = 'qaqc-tool-btn';
  const counter = document.createElement('span');
  counter.className = 'qaqc-counter';
  bar.appendChild(allBtn); bar.appendChild(clrBtn); bar.appendChild(counter);
  container.appendChild(bar);

  const rowRefreshers = [];
  const refreshCounter = () => {
    const rows = state.fields[field.id];
    const n = { pass: 0, fail: 0, na: 0 };
    rows.forEach(r => { if (r.result) n[r.result]++; });
    const done = n.pass + n.fail + n.na;
    counter.innerHTML = `${done}/${rows.length} inspected` +
      (n.fail ? ` · <span class="qaqc-counter-fail">${n.fail} FAIL</span>` : '') +
      (done === rows.length && !n.fail ? ' · <span class="qaqc-counter-ok">all good ✓</span>' : '');
  };

  allBtn.addEventListener('click', () => {
    state.fields[field.id].forEach(r => { r.result = 'pass'; });
    rowRefreshers.forEach(fn => fn());
    refreshCounter();
  });
  clrBtn.addEventListener('click', () => {
    state.fields[field.id].forEach(r => { r.result = null; });
    rowRefreshers.forEach(fn => fn());
    refreshCounter();
  });

  ITEMS.forEach((itemText, idx) => {
    const row = document.createElement('div');
    row.className = 'qaqc-row' + (idx % 2 === 0 ? '' : ' qaqc-row-alt');

    // Row number + text
    const header = document.createElement('div');
    header.className = 'qaqc-row-header';
    header.innerHTML = `<span class="qaqc-num">${idx + 1}</span><span class="qaqc-item-text">${itemText}</span>`;
    row.appendChild(header);

    // PASS / FAIL / N/A buttons
    const btns = document.createElement('div');
    btns.className = 'qaqc-btns';

    // Comment input (declared early so the button refresh can highlight it on FAIL)
    const commentInput = document.createElement('input');

    const refreshRow = () => {
      btns.querySelectorAll('.qaqc-btn').forEach((b, bi) => {
        const bval = ['pass', 'fail', 'na'][bi];
        b.className = 'qaqc-btn qaqc-' + bval + (state.fields[field.id][idx].result === bval ? ' active' : '');
      });
      const failed = state.fields[field.id][idx].result === 'fail';
      commentInput.style.borderColor = failed && !commentInput.value.trim() ? '#ef4444' : '';
      commentInput.placeholder = failed ? 'Required — describe the deficiency…' : 'Comments / deficiency notes…';
    };
    rowRefreshers.push(refreshRow);

    ['pass', 'fail', 'na'].forEach(val => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.textContent = val === 'na' ? 'N/A' : val.toUpperCase();
      btn.className = 'qaqc-btn qaqc-' + val + (state.fields[field.id][idx].result === val ? ' active' : '');
      btn.addEventListener('click', () => {
        const cur = state.fields[field.id][idx].result;
        state.fields[field.id][idx].result = (cur === val) ? null : val;
        refreshRow();
        refreshCounter();
      });
      btns.appendChild(btn);
    });
    row.appendChild(btns);

    const commentWrap = document.createElement('div');
    commentWrap.className = 'qaqc-comment-wrap';
    commentInput.type = 'text';
    commentInput.className = 'qaqc-comment-input';
    commentInput.placeholder = 'Comments / deficiency notes…';
    commentInput.value = state.fields[field.id][idx].comment;
    commentInput.addEventListener('input', e => {
      state.fields[field.id][idx].comment = e.target.value;
      if (state.fields[field.id][idx].result === 'fail') {
        commentInput.style.borderColor = e.target.value.trim() ? '' : '#ef4444';
      }
    });
    commentWrap.appendChild(commentInput);
    row.appendChild(commentWrap);

    container.appendChild(row);
  });

  refreshCounter();
}

// ── Production Report Table ───────────────────────────────────────────────────
const PROD_DAYS    = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
const BLOCK_TYPES  = ['8"', '4"', '6"', '10"', '12"'];
// Veneer is tracked in square feet (SF). Maps to material_types: "Veneer Stone" etc.
const VENEER_TYPES = ['Stone', 'Brick', 'Other'];

function emptyProdDays() {
  const d = {};
  PROD_DAYS.forEach(day => { d[day.toLowerCase()] = ''; });
  return d;
}

// Material catalog for the add-from-list production form. Loaded from
// material_types on first render; falls back to a hardcoded list if the DB
// call fails. Each item the foreman adds gets its own quantity + bricklayer
// + helper rows (so productivity can be computed per material later).
let _prodCatalog = [];
const PROD_CATALOG_FALLBACK = [
  { name: '8" Block',     category: 'block',  unit: 'count' },
  { name: '4" Block',     category: 'block',  unit: 'count' },
  { name: '6" Block',     category: 'block',  unit: 'count' },
  { name: '10" Block',    category: 'block',  unit: 'count' },
  { name: '12" Block',    category: 'block',  unit: 'count' },
  { name: 'Veneer Stone', category: 'veneer', unit: 'sf' },
  { name: 'Veneer Brick', category: 'veneer', unit: 'sf' },
  { name: 'Veneer Other', category: 'veneer', unit: 'sf' },
  { name: 'Brick',        category: 'brick',  unit: 'count' },
  { name: 'Firestop',     category: 'firestop', unit: 'lf' },
];

// Human label for a catalog unit ('count' shows as Qty in day inputs)
function _prodUnitLbl(unit) { return unit === 'sf' ? 'SF' : unit === 'lf' ? 'LF' : ''; }

function _prodCatalogList() { return _prodCatalog.length ? _prodCatalog : PROD_CATALOG_FALLBACK; }

function emptyProdItem(typeName, unit) {
  // bricklayers/helpers were collected here too — removed in 2026-06-01 since
  // labor cost comes from time_entries (in-house) or per-block sub rates (sub),
  // so per-material headcount on the production report was redundant.
  return {
    type:  typeName,
    unit:  unit || 'count',
    qty:   emptyProdDays(),
  };
}

// ══════════════════════════════════════════════════════════════════════════════
//  EXTRA WORK ORDER (EWO) — FOREMAN-SIDE RENDERERS
//  Foreman only enters WHO worked + HOW MANY HOURS, and WHAT materials/equipment
//  were used + HOW MUCH. No prices, no rates, no $ anywhere. Admin fills the
//  pricing layer from the "Extra Work" admin tab.
// ══════════════════════════════════════════════════════════════════════════════

// The foreman picks materials from the SAME canonical list the admin prices
// (names + units only — no prices ever reach the foreman). This keeps the
// description text identical so the admin's price picker matches by name.
// Loaded live from the public ewo_material_options view, cached for offline,
// and backed by this fallback (mirrors the seed) when neither is available.
const EWO_FALLBACK_MATERIALS = [
  { name: '4" Block', unit: 'unit' },  { name: '6" Block', unit: 'unit' },
  { name: '8" Block', unit: 'unit' },  { name: '10" Block', unit: 'unit' },
  { name: '12" Block', unit: 'unit' }, { name: 'Brick', unit: 'unit' },
  { name: 'Veneer Brick', unit: 'sf' },{ name: 'Veneer Stone', unit: 'sf' },
  { name: 'Veneer Other', unit: 'sf' },{ name: 'Mortar', unit: 'bag' },
  { name: 'Blockfill', unit: 'bag' },
];
function _ewoEsc(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g,
    c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
function _ewoReadCachedMatOptions() {
  try { const r = JSON.parse(localStorage.getItem('tmEwoMatOptions') || 'null'); return Array.isArray(r) && r.length ? r : null; } catch (_) { return null; }
}
let _ewoMatOptions = _ewoReadCachedMatOptions();
function _ewoOptions() { return _ewoMatOptions || EWO_FALLBACK_MATERIALS; }
async function _fetchEwoMatOptions() {
  try {
    const { data, error } = await sbClient.from('ewo_material_options').select('name, unit').order('sort');
    if (!error && Array.isArray(data) && data.length) {
      _ewoMatOptions = data.map(d => ({ name: d.name, unit: d.unit || '' }));
      try { localStorage.setItem('tmEwoMatOptions', JSON.stringify(_ewoMatOptions)); } catch (_) {}
      _populateEwoMatPicker();   // refresh the dropdown if it's on screen
    }
  } catch (_) { /* offline — cache / fallback already in use */ }
}
function _populateEwoMatPicker(sel) {
  sel = sel || document.getElementById('ewoMatPicker');
  if (!sel) return;
  const cur = sel.value;
  sel.innerHTML = '<option value="">+ Pick a material…</option>' +
    _ewoOptions().map(o => `<option value="${_ewoEsc(o.name)}">${_ewoEsc(o.name)}${o.unit ? ' (' + _ewoEsc(o.unit) + ')' : ''}</option>`).join('');
  sel.value = cur;
}

// Look up the next available EWO number for the currently selected project.
// Best-effort: failure leaves the placeholder visible — submit will retry.
async function _peekNextEwoNumber() {
  if (!state.project) return null;
  try {
    const { data: proj } = await sbClient.from('projects')
      .select('id').eq('name', state.project).maybeSingle();
    if (!proj?.id) return null;
    const { data, error } = await sbClient.rpc('next_ewo_number', { p_project_id: proj.id });
    if (error) { console.warn('peek ewo number:', error.message); return null; }
    return Number(data) || null;
  } catch (e) { console.warn('peek ewo number threw:', e); return null; }
}

function _ensureEwoState() {
  if (!Array.isArray(state.fields.ewo_labour))    state.fields.ewo_labour    = [];
  // Start empty — foreman adds the materials actually used via the picker.
  if (!Array.isArray(state.fields.ewo_materials)) state.fields.ewo_materials = [];
}

function _emptyEwoEmployee() {
  return { worker_id: '', worker_name: '', worker_trade: '',
           reg_hours: 0, ot_hours: 0, td_hours: 0 };
}

// ── Labour table — worker + hours only (no rate, no amount) ──────────────────
function renderEwoLabourTable(field, container) {
  _ensureEwoState();
  const header = document.createElement('div');
  header.className = 'section-header';
  header.textContent = field.label;
  container.appendChild(header);

  const body = document.createElement('div');
  body.className = 'section-body';

  // Helper banner so foreman knows pricing is the office's job
  const hint = document.createElement('div');
  hint.style.cssText = 'background:rgba(59,130,246,0.06);border-left:3px solid #3b82f6;padding:10px 12px;border-radius:6px;font-size:12px;color:#aaa;margin-bottom:12px;line-height:1.5';
  hint.innerHTML = '💼 Just record <strong>who worked</strong> and <strong>how many hours</strong>. Office will set rates and total cost when they review.';
  body.appendChild(hint);

  const rows = document.createElement('div');
  rows.id = 'ewoLabourRows';
  body.appendChild(rows);

  const addBtn = document.createElement('button');
  addBtn.type = 'button';
  addBtn.className = 'btn-add';
  addBtn.textContent = '+ Add Employee';
  addBtn.onclick = () => {
    state.fields.ewo_labour.push(_emptyEwoEmployee());
    _renderEwoLabourRows();
  };
  body.appendChild(addBtn);

  container.appendChild(body);
  _renderEwoLabourRows();
}

function _renderEwoLabourRows() {
  const wrap = document.getElementById('ewoLabourRows');
  if (!wrap) return;
  wrap.innerHTML = '';
  const list = state.fields.ewo_labour || [];
  if (!list.length) {
    const empty = document.createElement('div');
    empty.className = 'empty-hint';
    empty.textContent = 'No employees added yet. Tap "+ Add Employee" to start.';
    wrap.appendChild(empty);
  }
  list.forEach((row, i) => wrap.appendChild(_buildEwoLabourCard(row, i)));
}

function _buildEwoLabourCard(row, index) {
  const card = document.createElement('div');
  card.className = 'crew-card';

  // Header: worker dropdown + remove
  const head = document.createElement('div');
  head.className = 'crew-card-header';
  const num = document.createElement('span');
  num.className = 'crew-num';
  num.textContent = index + 1;
  head.appendChild(num);

  const sel = document.createElement('select');
  sel.className = 'field-input ts-name-select';
  sel.appendChild(new Option(`— Select employee ${index + 1} —`, ''));
  const workers = Array.isArray(window.workersFull) ? window.workersFull : [];
  workers.forEach(w => {
    const o = new Option(w.trade ? `${w.name} — ${w.trade}` : w.name, w.id);
    o.dataset.name  = w.name || '';
    o.dataset.trade = w.trade || '';
    if (row.worker_id === w.id) o.selected = true;
    sel.appendChild(o);
  });
  sel.addEventListener('change', () => {
    const opt = sel.selectedOptions[0];
    row.worker_id    = sel.value || '';
    row.worker_name  = opt?.dataset.name  || '';
    row.worker_trade = opt?.dataset.trade || '';
  });
  head.appendChild(sel);

  const rm = document.createElement('button');
  rm.type = 'button';
  rm.className = 'crew-remove';
  rm.textContent = '×';
  rm.onclick = () => {
    state.fields.ewo_labour.splice(index, 1);
    _renderEwoLabourRows();
  };
  head.appendChild(rm);
  card.appendChild(head);

  // Hours grid: Reg + OT inputs, Total is auto-computed (Reg + OT) — read-only.
  // td_hours is kept as a legacy DB column (always 0 for new rows) — TD was a
  // "Travel Day" premium from the Excel template that wasn't being used.
  const grid = document.createElement('div');
  grid.style.cssText = 'display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;margin-top:10px';

  const totalEl = document.createElement('div');
  totalEl.style.cssText = 'text-align:center;padding:8px;font-size:16px;font-weight:700;color:#3b82f6;background:rgba(59,130,246,0.06);border:1px solid rgba(59,130,246,0.25);border-radius:8px';
  totalEl.textContent = String((Number(row.reg_hours) || 0) + (Number(row.ot_hours) || 0));

  const recomputeTotal = () => {
    totalEl.textContent = String((Number(row.reg_hours) || 0) + (Number(row.ot_hours) || 0));
  };

  const mkNum = (label, key) => {
    const wrap = document.createElement('div');
    const lbl = document.createElement('label');
    lbl.style.cssText = 'font-size:11px;color:#888;display:block;margin-bottom:2px';
    lbl.textContent = label;
    const inp = document.createElement('input');
    inp.type = 'number'; inp.step = '0.25'; inp.min = '0';
    inp.className = 'field-input';
    inp.style.cssText = 'text-align:center;padding:8px;font-size:16px';
    inp.value = row[key] || '';
    inp.addEventListener('input', () => {
      row[key] = Number(inp.value) || 0;
      recomputeTotal();
    });
    wrap.appendChild(lbl); wrap.appendChild(inp);
    return wrap;
  };

  const totalWrap = document.createElement('div');
  const totalLbl = document.createElement('label');
  totalLbl.style.cssText = 'font-size:11px;color:#888;display:block;margin-bottom:2px';
  totalLbl.textContent = 'Total hrs';
  totalWrap.appendChild(totalLbl);
  totalWrap.appendChild(totalEl);

  grid.appendChild(mkNum('Reg hrs', 'reg_hours'));
  grid.appendChild(mkNum('OT hrs',  'ot_hours'));
  grid.appendChild(totalWrap);
  card.appendChild(grid);

  return card;
}

// ── Materials table — description + qty + unit only ─────────────────────────
function renderEwoMaterialsTable(field, container) {
  _ensureEwoState();

  const header = document.createElement('div');
  header.className = 'section-header';
  header.textContent = field.label;
  container.appendChild(header);

  const body = document.createElement('div');
  body.className = 'section-body';

  const hint = document.createElement('div');
  hint.style.cssText = 'background:rgba(59,130,246,0.06);border-left:3px solid #3b82f6;padding:10px 12px;border-radius:6px;font-size:12px;color:#aaa;margin-bottom:12px;line-height:1.5';
  hint.innerHTML = '🧱 Pick <strong>what was used</strong> and <strong>how much</strong>. Office will set vendor prices when they review.';
  body.appendChild(hint);

  // Refresh the canonical material names from the admin list (async; the
  // cached/fallback list shows immediately so the picker is never empty).
  _fetchEwoMatOptions();

  // Quick-add from the shared material list (keeps names matching the admin's)
  const pickRow = document.createElement('div');
  pickRow.style.cssText = 'display:flex;gap:8px;margin-bottom:12px';
  const sel = document.createElement('select');
  sel.id = 'ewoMatPicker';
  sel.className = 'field-input';
  sel.style.cssText = 'flex:1;min-width:0;padding:8px;font-size:15px';
  const addPick = document.createElement('button');
  addPick.type = 'button';
  addPick.className = 'btn-add';
  addPick.textContent = '+ Add';
  addPick.style.flex = '0 0 auto';
  addPick.onclick = () => {
    const name = sel.value; if (!name) return;
    const opt = _ewoOptions().find(o => o.name === name);
    state.fields.ewo_materials.push({ description: name, unit: opt?.unit || '', qty: 0, from_list: true, line_order: state.fields.ewo_materials.length });
    sel.value = '';
    _renderEwoMaterialRows();
  };
  pickRow.appendChild(sel);
  pickRow.appendChild(addPick);
  body.appendChild(pickRow);
  _populateEwoMatPicker(sel);

  const rows = document.createElement('div');
  rows.id = 'ewoMaterialRows';
  body.appendChild(rows);

  const addBtn = document.createElement('button');
  addBtn.type = 'button';
  addBtn.className = 'btn-add';
  addBtn.textContent = '+ Add other item (custom)';
  addBtn.onclick = () => {
    state.fields.ewo_materials.push({ description: '', qty: 0, unit: '', from_list: false, line_order: state.fields.ewo_materials.length });
    _renderEwoMaterialRows();
  };
  body.appendChild(addBtn);

  container.appendChild(body);
  _renderEwoMaterialRows();
}

function _renderEwoMaterialRows() {
  const wrap = document.getElementById('ewoMaterialRows');
  if (!wrap) return;
  wrap.innerHTML = '';
  (state.fields.ewo_materials || []).forEach((m, i) => wrap.appendChild(_buildEwoMaterialCard(m, i)));
}

function _buildEwoMaterialCard(m, index) {
  const card = document.createElement('div');
  card.className = 'crew-card';
  card.style.padding = '10px 12px';

  // Description line: read-only label for templates, editable for custom items
  const top = document.createElement('div');
  top.style.cssText = 'display:flex;align-items:center;gap:8px;margin-bottom:8px';
  if (m.from_list) {
    // Picked from the shared list — name is fixed so it matches the admin's list
    const desc = document.createElement('div');
    desc.style.cssText = 'font-weight:600;font-size:14px;flex:1';
    desc.textContent = m.description;
    top.appendChild(desc);
  } else {
    const inp = document.createElement('input');
    inp.type = 'text';
    inp.className = 'field-input';
    inp.placeholder = 'Description (e.g. Plywood sheets)';
    inp.style.cssText = 'padding:6px 8px;font-size:14px;flex:1';
    inp.value = m.description || '';
    inp.addEventListener('input', () => { m.description = inp.value; });
    top.appendChild(inp);
  }
  const rm = document.createElement('button');
  rm.type = 'button';
  rm.className = 'crew-remove';
  rm.textContent = '×';
  rm.onclick = () => {
    state.fields.ewo_materials.splice(index, 1);
    _renderEwoMaterialRows();
  };
  top.appendChild(rm);
  card.appendChild(top);

  // Qty + Unit (no rate, no amount)
  const grid = document.createElement('div');
  grid.style.cssText = 'display:grid;grid-template-columns:1fr 1.4fr;gap:8px;align-items:end';

  const qtyWrap = document.createElement('div');
  const qtyLbl = document.createElement('label');
  qtyLbl.style.cssText = 'font-size:10px;color:#888;display:block;margin-bottom:3px;text-transform:uppercase';
  qtyLbl.textContent = 'Qty';
  const qtyInp = document.createElement('input');
  qtyInp.type = 'number'; qtyInp.step = '0.01'; qtyInp.min = '0';
  qtyInp.className = 'field-input';
  qtyInp.style.cssText = 'padding:8px;font-size:16px;text-align:center';
  qtyInp.value = m.qty != null ? m.qty : '';
  qtyInp.addEventListener('input', () => { m.qty = Number(qtyInp.value) || 0; });
  qtyWrap.appendChild(qtyLbl); qtyWrap.appendChild(qtyInp);
  grid.appendChild(qtyWrap);

  const unitWrap = document.createElement('div');
  const unitLbl = document.createElement('label');
  unitLbl.style.cssText = 'font-size:10px;color:#888;display:block;margin-bottom:3px;text-transform:uppercase';
  unitLbl.textContent = 'Unit';
  const unitInp = document.createElement('input');
  unitInp.type = 'text';
  unitInp.className = 'field-input';
  unitInp.style.cssText = 'padding:8px;font-size:14px';
  unitInp.placeholder = 'each / bag / load / hr';
  unitInp.value = m.unit || '';
  unitInp.addEventListener('input', () => { m.unit = unitInp.value; });
  unitWrap.appendChild(unitLbl); unitWrap.appendChild(unitInp);
  grid.appendChild(unitWrap);

  card.appendChild(grid);
  return card;
}

function renderProductionTable(field, container) {
  const header = document.createElement('div');
  header.className = 'section-header';
  header.textContent = field.label;
  container.appendChild(header);

  const body = document.createElement('div');
  body.className = 'section-body';
  container.appendChild(body);

  if (!state.fields[field.id] || !Array.isArray(state.fields[field.id].items)) {
    state.fields[field.id] = { items: [] };
  }

  const addRow = document.createElement('div');
  addRow.className = 'prod-add-row';
  const addSelect = document.createElement('select');
  addSelect.className = 'field-input prod-add-select';
  addSelect.id = 'prodAddSelect';
  const addBtn = document.createElement('button');
  addBtn.type = 'button';
  addBtn.className = 'add-crew-btn';
  addBtn.textContent = '+ Add Material';
  const itemsWrap = document.createElement('div');
  itemsWrap.id = 'prodItemsWrap';

  addBtn.addEventListener('click', () => {
    const name = addSelect.value;
    if (!name) return;
    const cat = _prodCatalogList().find(c => c.name === name);
    state.fields[field.id].items.push(emptyProdItem(name, cat && cat.unit));
    _renderProdItems(field.id, itemsWrap, addSelect);
  });
  addRow.appendChild(addSelect);
  addRow.appendChild(addBtn);
  body.appendChild(addRow);
  body.appendChild(itemsWrap);

  (async () => {
    if (!_prodCatalog.length) {
      try {
        const { data } = await sbClient.from('material_types')
          .select('name, category, unit, sort_order').eq('active', true)
          .order('sort_order');
        _prodCatalog = (data && data.length) ? data : PROD_CATALOG_FALLBACK;
      } catch (_) { _prodCatalog = PROD_CATALOG_FALLBACK; }
    }
    _renderProdItems(field.id, itemsWrap, addSelect);
  })();
}

// Fill the "+ Add Material" dropdown via DOM (names contain " so innerHTML is unsafe)
function _populateProdAddSelect(fieldId, addSelect) {
  const used = new Set((state.fields[fieldId].items || []).map(i => i.type));
  const avail = _prodCatalogList().filter(c => !used.has(c.name));
  addSelect.innerHTML = '';
  const ph = document.createElement('option');
  ph.value = ''; ph.textContent = '+ choose material...';
  addSelect.appendChild(ph);
  avail.forEach(c => {
    const o = document.createElement('option');
    o.value = c.name;
    const u = _prodUnitLbl(c.unit);
    o.textContent = c.name + (u ? ` (${u})` : '');
    addSelect.appendChild(o);
  });
  addSelect.disabled = avail.length === 0;
}

function _renderProdItems(fieldId, wrap, addSelect) {
  const items = state.fields[fieldId].items || [];
  wrap.innerHTML = '';
  if (!items.length) {
    wrap.innerHTML = '<div class="prod-empty">No materials added yet. Pick one above and tap "+ Add Material".</div>';
  } else {
    items.forEach((_, idx) => _renderProdItemCard(fieldId, wrap, idx, addSelect));
  }
  _populateProdAddSelect(fieldId, addSelect);
}

function _renderProdItemCard(fieldId, wrap, idx, addSelect) {
  const item = state.fields[fieldId].items[idx];
  const card = document.createElement('div');
  card.className = 'prod-block-card';

  const head = document.createElement('div');
  head.className = 'prod-block-label-row';
  const title = document.createElement('span');
  title.className = 'prod-block-type';
  title.textContent = item.type + ' ';
  const unitBadge = document.createElement('span');
  unitBadge.className = 'prod-common-badge';
  unitBadge.style.background = item.unit === 'sf' ? '#3b82f6' : item.unit === 'lf' ? '#ef4444' : '#f59e0b';
  unitBadge.textContent = _prodUnitLbl(item.unit) || 'count';
  title.appendChild(unitBadge);
  const removeBtn = document.createElement('button');
  removeBtn.type = 'button';
  removeBtn.className = 'crew-remove';
  removeBtn.textContent = 'x';
  removeBtn.addEventListener('click', () => {
    state.fields[fieldId].items.splice(idx, 1);
    _renderProdItems(fieldId, wrap, addSelect);
  });
  head.appendChild(title);
  head.appendChild(removeBtn);
  card.appendChild(head);

  // Only quantity per day — bricklayer/helper headcount was removed; cost comes
  // from time_entries (in-house) or sub rates × production (sub).
  const subs = [
    ['qty', (_prodUnitLbl(item.unit) || 'Qty') + ' / day', item.unit === 'count' ? '1' : '0.1'],
  ];
  subs.forEach(function(s) {
    const key = s[0], label = s[1], step = s[2];
    const subLbl = document.createElement('div');
    subLbl.className = 'prod-sub-label';
    subLbl.textContent = label;
    card.appendChild(subLbl);

    const grid = document.createElement('div');
    grid.className = 'ts-days-grid';
    PROD_DAYS.forEach(day => {
      const dayKey = day.toLowerCase();
      const col = document.createElement('div');
      col.className = 'ts-day-col';
      const dl = document.createElement('div');
      dl.className = 'ts-day-label';
      dl.textContent = day.toUpperCase();
      const inp = document.createElement('input');
      inp.className = 'field-input ts-hours-input prod-block-input';
      inp.type = 'number'; inp.min = '0'; inp.step = step; inp.placeholder = '0';
      inp.value = item[key][dayKey] || '';
      inp.addEventListener('input', e => { state.fields[fieldId].items[idx][key][dayKey] = e.target.value; });
      col.appendChild(dl); col.appendChild(inp);
      grid.appendChild(col);
    });
    card.appendChild(grid);
  });

  wrap.appendChild(card);
}

// ── Photo Upload ──────────────────────────────────────────────────────────────
function setupPhotoUpload() {
  const zone = document.getElementById('photoZone');
  const input = document.getElementById('photoInput');
  if (!zone || !input) return;

  zone.addEventListener('click', () => input.click());
  zone.addEventListener('dragover', e => { e.preventDefault(); zone.classList.add('drag-over'); });
  zone.addEventListener('dragleave', () => zone.classList.remove('drag-over'));
  zone.addEventListener('drop', e => { e.preventDefault(); zone.classList.remove('drag-over'); addPhotos(Array.from(e.dataTransfer.files)); });
  input.addEventListener('change', () => { addPhotos(Array.from(input.files)); input.value = ''; });
  renderPhotoPreview();
}

function addPhotos(files) {
  state.photos = [...state.photos, ...files.filter(f => f.type.startsWith('image/'))].slice(0, 15);
  renderPhotoPreview();
}

function renderPhotoPreview() {
  const preview = document.getElementById('photoPreview');
  const countEl = document.getElementById('photoCount');
  if (!preview) return;
  preview.innerHTML = '';
  if (countEl) countEl.textContent = state.photos.length > 0 ? `${state.photos.length} photo${state.photos.length !== 1 ? 's' : ''} selected` : '';
  state.photos.forEach((file, i) => {
    const item = document.createElement('div');
    item.className = 'photo-thumb';
    const img = document.createElement('img');
    img.src = URL.createObjectURL(file);
    const remove = document.createElement('button');
    remove.className = 'photo-remove';
    remove.textContent = '×';
    remove.addEventListener('click', e => { e.stopPropagation(); state.photos.splice(i, 1); renderPhotoPreview(); });
    item.appendChild(img);
    item.appendChild(remove);
    preview.appendChild(item);
  });
}

// ── Step 4: Review ────────────────────────────────────────────────────────────
async function renderStep4() {
  const typeInfo = SUBMISSION_TYPES.find(t => t.id === state.submissionType);
  document.getElementById('reviewForeman').textContent = state.foremanName;
  document.getElementById('reviewDate').textContent = state.date;
  document.getElementById('reviewProject').textContent = state.project;
  document.getElementById('reviewType').textContent = state.submissionType;
  document.getElementById('reviewPhotos').textContent = state.photos.length > 0 ? `${state.photos.length} photo(s)` : 'No photos';
  const safeName  = (state.submissionType || '').replace(/\s+/g, '-');
  const safeProj  = (state.project || '').replace(/[^a-zA-Z0-9\-_ ]/g, '').trim().replace(/\s+/g, '-');
  const pdfName   = `${state.date}_${safeName}_${safeProj}.pdf`;
  const pathEl    = document.getElementById('reviewOneDrivePath');
  if (pathEl) { pathEl.textContent = pdfName; pathEl.style.opacity = '1'; }
  const pathLabel = pathEl ? pathEl.previousElementSibling : null;
  if (pathLabel) pathLabel.textContent = '⬇️ Will download as';

  const sigPreview = document.getElementById('reviewSignature');
  if (sigPreview) {
    if (state.signature) {
      sigPreview.innerHTML = `<img src="${state.signature}" style="max-width:200px;border:1px solid #333;border-radius:6px;background:#fff;padding:4px">`;
    } else {
      sigPreview.textContent = 'No signature';
    }
  }

  // ── Load CSO contact + render send-to-safety toggle ──────────────────────
  state._csoContact = null;
  try {
    const { data } = await sbClient
      .from('project_safety_contacts')
      .select('cso_email, cso_name')
      .eq('project_name', state.project)
      .maybeSingle();
    state._csoContact = data || null;
  } catch (e) { /* non-fatal */ }

  const toggleWrap = document.getElementById('reviewSafetyToggle');
  if (toggleWrap && !_isSafetyEmailForm(state.submissionType)) {
    // Non-safety forms never email the safety officer — hide the toggle entirely.
    toggleWrap.innerHTML = '';
  } else if (toggleWrap) {
    if (state._csoContact) {
      const who = [state._csoContact.cso_name, state._csoContact.cso_email].filter(Boolean).join(' · ');
      toggleWrap.innerHTML = `
        <div style="display:flex;align-items:center;gap:12px;padding:12px 14px;
          background:rgba(0,32,91,.12);border:1px solid rgba(0,32,91,.3);
          border-radius:10px;">
          <label style="display:flex;align-items:center;gap:8px;cursor:pointer;flex:1;user-select:none;">
            <input type="checkbox" id="sendToSafetyChk" ${state.sendToSafety ? 'checked' : ''}
              style="width:16px;height:16px;accent-color:#00205b;cursor:pointer;flex-shrink:0;">
            <span style="font-size:13px;font-weight:600;color:var(--text);">📧 Send to Safety Officer</span>
          </label>
          <span style="font-size:11px;color:var(--text-muted);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:180px;"
            title="${who}">${who}</span>
        </div>`;
      document.getElementById('sendToSafetyChk').addEventListener('change', e => {
        state.sendToSafety = e.target.checked;
      });
    } else {
      toggleWrap.innerHTML = `
        <div style="padding:10px 14px;background:rgba(245,158,11,.08);border:1px solid rgba(245,158,11,.25);
          border-radius:10px;font-size:12px;color:var(--text-muted);">
          ⚠ No safety contact configured for <strong>${state.project}</strong> — add one in the Admin panel.
        </div>`;
    }
  }

  const summary = document.getElementById('reviewFieldSummary');
  if (!summary) return;
  summary.innerHTML = '';
  const fields = FORM_FIELDS[state.submissionType] || [];
  const skipTypes = ['tailgate-items','flra-table','closeout','crew-signin','signature'];
  fields.filter(f => !skipTypes.includes(f.type)).forEach(f => {
    const val = state.fields[f.id];
    if (!val || (Array.isArray(val) && val.length === 0) || val === '') return;
    const row = document.createElement('div');
    row.className = 'review-row';
    row.innerHTML = `<span class="review-label">${f.label}</span><span class="review-value">${Array.isArray(val) ? val.join(', ') : val}</span>`;
    summary.appendChild(row);
  });
  if (summary.children.length === 0) summary.innerHTML = '<p class="muted">Form ready to submit.</p>';
}

// ── Validation ────────────────────────────────────────────────────────────────
function validateStep(step) {
  clearErrors();

  if (step === 1) {
    const name = document.getElementById('foremanName').value.trim();
    const date = document.getElementById('foremanDate').value;
    if (!name) { showError('foremanName', 'Your name is required.'); return false; }
    if (!date) { showError('foremanDate', 'Please select a date.'); return false; }
    // Safety net: the date picker has max=today, but a foreman could still paste
    // or type a future date directly. Refuse it — this is what caused Dez's
    // 65 blocks to land on Jun 26 instead of May 29.
    const today = _todayLocal();
    if (date > today) {
      showError('foremanDate', "Date can't be in the future. Today is the latest.");
      // also refresh the max in case the tab has been open past midnight
      document.getElementById('foremanDate').max = today;
      return false;
    }
    state.foremanName = name;
    state.date = date;
    return true;
  }

  if (step === 2) {
    if (!state.project) { showError('projectSelect', 'Please select a project.'); return false; }
    if (!state.submissionType) { showGlobalError('Please select a submission type.'); return false; }
    return true;
  }

  if (step === 3) {
    // Site Photos has its own validation inside handleSitePhotosUpload()
    if (state.submissionType === 'Site Photos Only') return true;

    const fields = FORM_FIELDS[state.submissionType] || [];

    // QAQC checklists: every item must be inspected, and a FAIL needs a
    // comment describing the deficiency — a half-blank sheet can't go to a GC.
    for (const field of fields) {
      if (field.type !== 'qaqc-table') continue;
      const rows = state.fields[field.id] || [];
      const missing = rows.findIndex(r => !r || !r.result);
      if (missing >= 0) {
        showGlobalError(`${field.label}: item ${missing + 1} not inspected yet — mark PASS, FAIL or N/A.`);
        return false;
      }
      const failNoComment = rows.findIndex(r => r && r.result === 'fail' && !(r.comment || '').trim());
      if (failNoComment >= 0) {
        showGlobalError(`${field.label}: item ${failNoComment + 1} FAILED — add a comment describing the deficiency.`);
        return false;
      }
    }

    // Weekly Timesheet: the name picker is dropdown-only precisely so every
    // row maps to a real worker_id and syncTimesheetToDb never has to guess —
    // but a foreman can still type hours into a row and never actually pick
    // a name (e.g. the list was still loading, or they just missed it). That
    // row's hours would submit fine, generate a normal PDF, and then vanish
    // silently — no time_entries row, so it never reaches the dashboard.
    for (const field of fields) {
      if (field.type !== 'timesheet-table') continue;
      const rows = state.fields[field.id] || [];
      const badIdx = rows.findIndex(emp => {
        if (emp.worker_id) return false;
        const days = emp.days || {};
        return Object.values(days).some(d => (parseFloat(d?.r) || 0) > 0 || (parseFloat(d?.ot) || 0) > 0 || d?.sick);
      });
      if (badIdx >= 0) {
        const who = rows[badIdx].name ? ` ("${rows[badIdx].name}")` : '';
        showGlobalError(`Employee ${badIdx + 1}${who} has hours entered but isn't selected from the dropdown — those hours won't count on the dashboard. Pick their name from the list.`);
        document.getElementById(`tsEmp${badIdx}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return false;
      }
    }

    for (const field of fields) {
      if (!field.required) continue;
      if (field.type === 'signature') {
        // Capture from pad at this exact moment (most reliable)
        if (window._signaturePad && !window._signaturePad.isEmpty()) {
          state.signature = window._signaturePad.toDataURL('image/png');
        }
        if (!state.signature) {
          showGlobalError('Please sign the document before continuing.');
          document.getElementById('signatureCanvas')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return false;
        }
        continue;
      }
      const val = state.fields[field.id];
      if (!val || (Array.isArray(val) && val.length === 0) || val === '') {
        showGlobalError(`Please fill in: ${field.label}`);
        return false;
      }
    }
    return true;
  }

  return true;
}

function showError(id, msg) {
  const el = document.getElementById(id);
  if (el) {
    el.classList.add('field-error');
    const err = document.createElement('span');
    err.className = 'error-msg';
    err.textContent = msg;
    el.parentNode.appendChild(err);
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}

function showGlobalError(msg) {
  const toast = document.getElementById('errorToast');
  if (toast) {
    toast.textContent = msg;
    toast.className = 'toast toast-error visible';
    setTimeout(() => toast.classList.remove('visible'), 4000);
  }
}

function clearErrors() {
  document.querySelectorAll('.field-error').forEach(el => el.classList.remove('field-error'));
  document.querySelectorAll('.error-msg').forEach(el => el.remove());
}

// ── Auto-download helper ──────────────────────────────────────────────────────
function buildPDFFilename() {
  const type = (state.submissionType || 'Report').replace(/\s+/g, '-');
  const proj = (state.project || '').replace(/[^a-zA-Z0-9\- ]/g, '').trim().replace(/\s+/g, '-');
  // Append HHMMSS so two submissions on the same date+type+project don't
  // collide in Storage (would 400 Bad Request on upload with upsert: false).
  const now = new Date();
  const hhmmss = String(now.getHours()).padStart(2, '0')
               + String(now.getMinutes()).padStart(2, '0')
               + String(now.getSeconds()).padStart(2, '0');
  const base = proj ? `${state.date}_${type}_${proj}` : `${state.date}_${type}`;
  return `${base}_${hhmmss}.pdf`;
}

function triggerDownload(buffer, filename) {
  try {
    const blob = new Blob([buffer], { type: 'application/pdf' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  } catch (e) {
    console.warn('Auto-download failed:', e);
  }
}

// ── Signature compression (PNG → small JPEG before PDF) ──────────────────────
function compressSig(dataUrl, maxW = 380, maxH = 100, quality = 0.55) {
  return new Promise(resolve => {
    if (!dataUrl || !dataUrl.startsWith('data:image')) { resolve(dataUrl); return; }
    const img = new Image();
    img.onload = () => {
      const ratio = Math.min(maxW / img.width, maxH / img.height, 1);
      const w = Math.max(1, Math.round(img.width  * ratio));
      const h = Math.max(1, Math.round(img.height * ratio));
      const cv = document.createElement('canvas');
      cv.width = w; cv.height = h;
      const ctx = cv.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0, w, h);
      resolve(cv.toDataURL('image/jpeg', quality));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

// ── Draft Save / Load ────────────────────────────────────────────────────────
// Every form that takes the foreman more than 30 seconds to fill has draft
// support — auto-save every 30s, plus a manual Save Progress button.
// Weekly forms (Timesheet, Production Report) scope drafts by the Monday of
// the week. Everything else scopes by the day so two Daily Tailgates on the
// same week don't trample each other.
const DRAFTABLE_FORMS = [
  'Weekly Timesheet',
  'Production Report',
  'Daily Tailgate',
  'Weekly Toolbox Talk',
  'Incident Report',
  'Hazard Observation',
  'QAQC - Foreman',
  'Field QAQC - ITP',
  'Telehandler Inspection',
  'Forklift Inspection',
  'E-Pallet Jack Inspection',
  'Scaffolding Inspection',
  'Extra Work Order',
];
const WEEKLY_DRAFT_FORMS = ['Weekly Timesheet', 'Production Report'];

function currentWeekMonday() {
  const d = new Date();
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  return d.toISOString().slice(0, 10);
}

// Pick the right scope key for the draft: Monday of the week for weekly forms,
// today's local date for everything else.
function _draftScopeFor(formType) {
  if (WEEKLY_DRAFT_FORMS.includes(formType)) return currentWeekMonday();
  return _todayLocal();
}

async function saveDraft(opts = {}) {
  if (!window.currentUser) return;
  const scope = _draftScopeFor(state.submissionType);
  const fieldsToSave = Object.assign({}, state.fields);
  delete fieldsToSave.signature;     // never persist signatures
  delete fieldsToSave.gcSignature;

  const { error } = await sbClient.from('drafts').upsert({
    foreman_id: window.currentUser.id,
    form_type:  state.submissionType,
    week_of:    scope,
    form_data:  fieldsToSave,
    updated_at: new Date().toISOString()
  }, { onConflict: 'foreman_id,form_type,week_of' });

  if (error) {
    if (!opts.silent) showToast('Could not save draft.', 'error');
    console.warn('Draft save failed:', error);
    return;
  }
  if (!opts.silent) showToast('Progress saved!', 'success');
  const ts = document.getElementById('draftSavedAt');
  if (ts) ts.textContent = 'Last saved ' + new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

async function loadDraftData(formType) {
  if (!window.currentUser) return null;
  const scope = _draftScopeFor(formType);
  const { data, error } = await sbClient
    .from('drafts')
    .select('form_data, updated_at')
    .eq('foreman_id', window.currentUser.id)
    .eq('form_type', formType)
    .eq('week_of', scope)
    .maybeSingle();
  if (error || !data) return null;
  return data;
}

async function deleteDraft() {
  if (!window.currentUser) return;
  await sbClient.from('drafts').delete()
    .eq('foreman_id', window.currentUser.id)
    .eq('form_type', state.submissionType)
    .eq('week_of', _draftScopeFor(state.submissionType));
}

// ── Auto-save heartbeat — fires every 30s while on Step 3 of a draftable form
let _autoSaveTimer = null;
function _startAutoSaveDraft() {
  _stopAutoSaveDraft();
  _autoSaveTimer = setInterval(() => {
    if (!DRAFTABLE_FORMS.includes(state.submissionType)) { _stopAutoSaveDraft(); return; }
    // Silent save — don't toast every 30s. Banner timestamp updates.
    saveDraft({ silent: true }).catch(err => console.warn('auto-save failed:', err));
  }, 30000);
}
function _stopAutoSaveDraft() {
  if (_autoSaveTimer) { clearInterval(_autoSaveTimer); _autoSaveTimer = null; }
}

async function clearDraftAndReset() {
  await deleteDraft();
  state.fields = {};
  renderStep3();
  showToast('Draft cleared.', 'info');
}

// ══════════════════════════════════════════════════════════════════════════════
//  SITE PHOTOS — dedicated lightweight flow (no PDF, direct to Storage)
// ══════════════════════════════════════════════════════════════════════════════
let _spPhotos = []; // [{file, caption, objectUrl}]

function renderSitePhotosStep() {
  const titleEl = document.getElementById('formTypeTitle');
  if (titleEl) titleEl.textContent = '📸 Site Photos';

  const container = document.getElementById('dynamicFields');
  if (!container) return;

  // Hide the standard photo-section (always present in step 3 HTML)
  const photoSection = document.querySelector('.photo-section');
  if (photoSection) photoSection.style.display = 'none';

  // Release any previous object URLs
  _spPhotos.forEach(p => URL.revokeObjectURL(p.objectUrl));
  _spPhotos = [];

  container.innerHTML = `
    <div id="spPhotoList" class="sp-photo-list"></div>
    <div class="sp-add-row">
      <button type="button" class="sp-cam-btn" onclick="document.getElementById('spFileInput').click()">📷</button>
      <input id="spCapInput" type="text" class="sp-cap-input" placeholder="Add a caption, then tap 📷 to add photo…">
      <input type="file" id="spFileInput" accept="image/*" multiple hidden>
    </div>
  `;

  document.getElementById('spFileInput').addEventListener('change', e => {
    const caption = (document.getElementById('spCapInput')?.value || '').trim();
    Array.from(e.target.files)
      // Accept image/* types AND HEIC (iOS sometimes reports empty type for HEIC)
      .filter(f => f.type.startsWith('image/') || f.name.toLowerCase().endsWith('.heic') || f.name.toLowerCase().endsWith('.heif'))
      .forEach(f => _spPhotos.push({ file: f, caption, objectUrl: URL.createObjectURL(f) }));
    e.target.value = '';
    const capEl = document.getElementById('spCapInput');
    if (capEl) capEl.value = '';
    _renderSpList();
  });

  _renderSpList();
}

function _renderSpList() {
  const list = document.getElementById('spPhotoList');
  if (!list) return;
  if (_spPhotos.length === 0) {
    list.innerHTML = '<p class="sp-empty">No photos yet — type a caption and tap 📷 to add.</p>';
    return;
  }
  list.innerHTML = _spPhotos.map((p, i) => `
    <div class="sp-photo-item">
      <div class="sp-thumb-wrap">
        <img class="sp-thumb" src="${p.objectUrl}" alt="">
        <button type="button" class="sp-remove" onclick="spRemovePhoto(${i})">×</button>
      </div>
      <input class="sp-caption field-input" type="text"
        placeholder="Caption for this photo (optional)"
        value="${(p.caption||'').replace(/"/g,'&quot;')}"
        oninput="_spPhotos[${i}].caption = this.value">
    </div>
  `).join('');
}

function spRemovePhoto(i) {
  URL.revokeObjectURL(_spPhotos[i].objectUrl);
  _spPhotos.splice(i, 1);
  _renderSpList();
}

async function handleSitePhotosUpload() {
  const btn   = document.getElementById('btnNext');
  const errEl = document.getElementById('globalError');

  if (_spPhotos.length === 0) {
    showToast('Add at least one photo before uploading.', 'warning');
    return;
  }

  btn.disabled = true;
  btn.textContent = 'Uploading…';
  if (errEl) errEl.hidden = true;

  try {
    const { data: project } = await sbClient.from('projects')
      .select('id').eq('name', state.project).single();
    if (!project) throw new Error('Project not found — go back and re-select.');

    const date = new Date().toISOString().split('T')[0];
    let uploaded = 0;

    for (const p of _spPhotos) {
      const ext        = (p.file.name.split('.').pop() || 'jpg').toLowerCase();
      const mimeType   = p.file.type || (ext === 'heic' ? 'image/heic' : 'image/jpeg');
      const safeName   = `${date}_${Date.now()}_${Math.random().toString(36).slice(2,7)}.${ext}`;
      const storagePath = `${project.id}/site-photos/${safeName}`;

      const { error: upErr } = await sbClient.storage
        .from('project-documents')
        .upload(storagePath, p.file, { contentType: mimeType, upsert: false });

      if (upErr) { console.warn('Photo upload skipped:', p.file.name, upErr.message); continue; }

      // Bucket is public — use getPublicUrl (no auth required, no expiry)
      const { data: { publicUrl } } = sbClient.storage
        .from('project-documents')
        .getPublicUrl(storagePath);

      const title = p.caption.trim() || 'Site Photo';
      const { error: dbErr } = await sbClient.from('documents').insert({
        project_id:  project.id,
        title,
        type:        'site_photo',
        file_name:   storagePath,
        file_url:    publicUrl,
        uploaded_by: window.currentUser?.id
      });
      if (dbErr) { console.warn('Document record failed:', dbErr.message); continue; }
      uploaded++;
    }

    // Log submission
    try {
      await sbClient.from('submissions').insert({
        foreman_id:      _effectiveForemanId(),
        foreman_name:    _effectiveForemanName(),
        project_name:    state.project,
        submission_type: 'Site Photos Only'
      });
    } catch (_) {}

    // Show success in place
    const container = document.getElementById('dynamicFields');
    if (container) {
      container.innerHTML = `
        <div class="success-screen">
          <div class="success-icon">✅</div>
          <h2 class="success-title">${uploaded} Photo${uploaded !== 1 ? 's' : ''} Uploaded!</h2>
          <p class="success-msg">Saved to Site Documentation for <strong>${state.project}</strong>.</p>
          <button class="btn btn-next" onclick="resetApp()" style="margin-top:24px;width:100%">+ New Submission</button>
        </div>`;
    }
    btn.style.display = 'none';
    document.getElementById('btnBack').style.display = 'none';

  } catch (err) {
    console.error(err);
    btn.disabled = false;
    btn.textContent = '📤 Upload Photos';
    showGlobalError('Upload failed: ' + err.message);
  }
}

// ── Submit ────────────────────────────────────────────────────────────────────
// Only genuine safety / inspection forms go to the safety inbox. Production,
// timesheets, extra work, deliveries, photos and QAQC are stored + synced to
// the dashboard but never emailed to the safety officer.
const SAFETY_EMAIL_TYPES = new Set([
  'Daily Tailgate', 'Weekly Toolbox Talk', 'Incident Report', 'Hazard Observation',
  'Telehandler Inspection', 'Forklift Inspection', 'E-Pallet Jack Inspection', 'Scaffolding Inspection',
]);
function _isSafetyEmailForm(type) { return SAFETY_EMAIL_TYPES.has(type); }

async function handleSubmit() {
  // Site Photos uses its own upload flow (no PDF, no email)
  if (state.submissionType === 'Site Photos Only') {
    await handleSitePhotosUpload();
    return;
  }
  const btn = document.getElementById('btnNext');
  btn.disabled = true;
  btn.textContent = 'Generating PDF...';
  showToast('Generating PDF report...', 'info');

  try {
    // Capture signatures from pads at submit time (in case endStroke didn't fire)
    if (window._signaturePad && !window._signaturePad.isEmpty()) {
      state.signature = window._signaturePad.toDataURL('image/png');
    }
    if (window._gcSignaturePad && !window._gcSignaturePad.isEmpty()) {
      state.gcSignature = window._gcSignaturePad.toDataURL('image/png');
    }

    // Compress signature PNGs → small JPEGs to reduce PDF + DB size
    if (state.signature)   state.signature   = await compressSig(state.signature);
    if (state.gcSignature) state.gcSignature = await compressSig(state.gcSignature);
    // Compress crew sign-in member signatures too
    if (Array.isArray(state.fields['crew-signin'])) {
      state.fields['crew-signin'] = await Promise.all(
        state.fields['crew-signin'].map(async m =>
          m.sig ? { ...m, sig: await compressSig(m.sig) } : m
        )
      );
    }

    // Auto-fill "Workers on site" from the crew sign-in count so the PDF
    // header and the email metadata both reflect who actually attended. This
    // covers Daily Tailgate + Weekly Toolbox Talk (the two forms with crew
    // sign-in); other form types stay blank and render "—" as before.
    if (!state.workersOnSite && Array.isArray(state.fields?.['crew-signin'])) {
      const count = state.fields['crew-signin'].filter(m => (m?.name || '').trim()).length;
      if (count > 0) state.workersOnSite = String(count);
    }

    // Make sure the PDF generator + jsPDF + autotable have finished loading.
    // If the pre-warm on Step 3 was fast enough this is an instant no-op;
    // worst case it waits for the network — slower phones / first visits.
    btn.textContent = 'Loading PDF tools...';
    await _ensurePdfDeps();
    btn.textContent = 'Generating PDF...';

    // Weekly Timesheet: flag sick days beyond the 5/year allowance as unpaid
    // BEFORE the PDF is built, so the PDF and the hours sync agree. If the
    // balance check fails (offline / transient), default to paid — overpaying
    // a sick day beats blocking the whole submission.
    try { await _annotateUnpaidSickDays(state); }
    catch (e) { console.warn('sick allowance check failed (defaulting to paid):', e); }

    const pdfBuffer   = await generatePDF(state, state.photos);
    const pdfFilename = buildPDFFilename();
    // Log PDF size for monitoring (visible in browser console)
    const sizeKB = Math.round(pdfBuffer.byteLength / 1024);
    console.log(`[PDF] ${pdfFilename} — ${sizeKB} KB (${(sizeKB/1024).toFixed(2)} MB) | ${state.photos?.length || 0} photo(s)`);

    // ── Offline shortcut ──────────────────────────────────────────────────────
    // If this form supports offline use and there's no connection, queue it now
    // (the PDF is already built) and skip every network step. It will send itself
    // automatically when connectivity returns. See js/offline.js.
    const _offlineEligible = (typeof OFFLINE_FORMS !== 'undefined') && OFFLINE_FORMS.includes(state.submissionType);
    if (_offlineEligible && navigator.onLine === false && typeof saveSubmissionOffline === 'function') {
      await saveSubmissionOffline(state, pdfBuffer, pdfFilename);
      triggerDownload(pdfBuffer, pdfFilename);
      if (DRAFTABLE_FORMS.includes(state.submissionType)) deleteDraft().catch(() => {});
      showOfflineSavedScreen(pdfFilename);
      return;
    }

    // ── Upload PDF to Supabase Storage ────────────────────────────────────────
    let pdfStorageUrl  = null;
    let pdfStoragePath = null;
    try {
      if (window.currentUser) {
        const fileName = `${window.currentUser.id}/${pdfFilename}`;
        pdfStoragePath = fileName; // always store path so admin can regenerate URL
        const pdfBlob  = new Blob([pdfBuffer], { type: 'application/pdf' });
        const { error: upErr } = await sbClient.storage
          .from('form-submissions')
          .upload(fileName, pdfBlob, { contentType: 'application/pdf', upsert: false });
        if (!upErr) {
          const { data: urlData } = await sbClient.storage
            .from('form-submissions')
            .createSignedUrl(fileName, 60 * 60 * 24 * 365); // 1 year
          pdfStorageUrl = urlData?.signedUrl || null;
        }
      }
    } catch (upErr) {
      console.warn('PDF upload to storage failed:', upErr);
    }

    // ── Log submission to Supabase ────────────────────────────────────────────
    // For Weekly Timesheet + Production Report we also stamp `week_of` (Monday)
    // so the admin can pair them up by (project, foreman, week_of) and see
    // which weeks have one report but not the other.
    let _submissionId = null;
    try {
      if (window.currentUser) {
        let weekOfIso = null;
        if (state.submissionType === 'Weekly Timesheet' || state.submissionType === 'Production Report') {
          const ws = _weekStartFor(state.date);
          weekOfIso = ws.getFullYear() + '-' +
                      String(ws.getMonth() + 1).padStart(2, '0') + '-' +
                      String(ws.getDate()).padStart(2, '0');
        }
        // Supabase returns { data, error } — destructure BOTH so an RLS rejection
        // (or any other DB error) is logged loudly instead of being swallowed
        // by the outer try/catch (which only catches thrown exceptions).
        const { data: subRow, error: subErr } = await sbClient.from('submissions').insert({
          foreman_id:      _effectiveForemanId(),
          foreman_name:    _effectiveForemanName(),
          project_name:    state.project,
          submission_type: state.submissionType,
          pdf_url:         pdfStorageUrl,
          pdf_path:        pdfStoragePath,
          week_of:         weekOfIso,
        }).select('id').single();
        if (subErr) {
          console.error('[submissions insert] failed (Activity log row missing):', subErr);
          showToast('Activity log entry failed — please notify admin', 'warning');
        }
        _submissionId = subRow?.id || null;
      }
    } catch (logErr) {
      console.warn('Could not log submission to Supabase:', logErr);
    }

    // ── Structured per-day save for the production dashboard ──────────────────
    // Production Report + Weekly Timesheet are submitted as a weekly batch in
    // the form, but the admin dashboard needs per-day rows. We expand the
    // weekly form into 7 day rows in production_reports / time_entries here.
    // submission_id links them so deleting the submission cascades the cleanup.
    // Failure is logged but doesn't block the submission — PDF + email still go.
    if (window.currentUser && state.fields) {
      try {
        if (state.submissionType === 'Production Report') {
          await syncProductionReportToDb(state, _submissionId);
        } else if (state.submissionType === 'Weekly Timesheet') {
          await syncTimesheetToDb(state, _submissionId);
        } else if (state.submissionType === 'Extra Work Order') {
          await syncExtraWorkOrderToDb(state, _submissionId);
        }
      } catch (syncErr) {
        console.warn('Could not sync structured data for dashboard:', syncErr);
      }
    }

    // Need connectivity to have stored the PDF + submission above. If we lost
    // it, queue offline-eligible forms; otherwise surface an error.
    const online = await checkBackendHealth();
    if (!online) {
      if (_offlineEligible && typeof saveSubmissionOffline === 'function') {
        await saveSubmissionOffline(state, pdfBuffer, pdfFilename);
        triggerDownload(pdfBuffer, pdfFilename);
        if (DRAFTABLE_FORMS.includes(state.submissionType)) deleteDraft().catch(() => {});
        showOfflineSavedScreen(pdfFilename);
        return;
      }
      throw new Error('Server is offline. Please try again in a moment.');
    }

    // ── Non-safety forms: stored + synced already, no safety email. Download. ──
    if (!_isSafetyEmailForm(state.submissionType)) {
      triggerDownload(pdfBuffer, pdfFilename);
      if (DRAFTABLE_FORMS.includes(state.submissionType)) deleteDraft().catch(() => {});
      showSuccessScreen({ message: 'Saved — no safety email sent for this form', filesAttached: 1 }, pdfFilename);
      return;
    }

    // ── Safety / inspection forms — email the safety inbox (+ CSO if chosen) ──
    btn.textContent = 'Sending email...';
    showToast('Sending email...', 'info');

    try {
      const result = await submitForm(state, pdfBuffer, state.photos);

      // ── Download PDF only after email is confirmed sent ─────────────────────
      triggerDownload(pdfBuffer, pdfFilename);

      // ── Delete draft on successful submission ───────────────────────────────
      if (DRAFTABLE_FORMS.includes(state.submissionType)) {
        deleteDraft().catch(() => {});
      }

      showSuccessScreen(result, pdfFilename);
    } catch (netErr) {
      // Connection dropped mid-submit. For offline-capable forms, save it locally
      // instead of losing the foreman's work — it'll send on reconnect.
      const looksOffline = navigator.onLine === false ||
        /offline|network|failed to fetch|load failed|fetch/i.test(netErr?.message || '');
      if (_offlineEligible && looksOffline && typeof saveSubmissionOffline === 'function') {
        await saveSubmissionOffline(state, pdfBuffer, pdfFilename);
        triggerDownload(pdfBuffer, pdfFilename);
        if (DRAFTABLE_FORMS.includes(state.submissionType)) deleteDraft().catch(() => {});
        showOfflineSavedScreen(pdfFilename);
        return;
      }
      throw netErr;
    }

  } catch (err) {
    console.error(err);
    btn.disabled = false;
    btn.textContent = '📤 Submit Form';
    showGlobalError(`Submission failed: ${err.message}`);
  }
}

function showSuccessScreen(result, pdfFilename) {
  const panel = document.getElementById('step4');
  if (!panel) return;
  panel.innerHTML = `
    <div class="success-screen">
      <div class="success-icon">✅</div>
      <h2 class="success-title">Submitted!</h2>
      <p class="success-msg">Email sent successfully!<br><code>${result.message || ''}</code></p>
      <p class="success-meta">${result.filesAttached ? `${result.filesAttached} file(s) attached` : ''}</p>
      ${pdfFilename ? `<div class="success-saved-path"><span class="success-saved-icon">⬇️</span><span class="success-saved-text">${pdfFilename} downloaded</span></div>` : ''}
      <button class="btn btn-next" onclick="resetApp()" style="margin-top:24px;width:100%">+ New Submission</button>
    </div>`;
}

function resetApp() {
  _stopAutoSaveDraft();
  state.currentStep = 1;
  state.foremanName = '';
  state.project = '';
  state.submissionType = '';
  state.allowedTypes = null;
  state.fields = {};
  state.photos = [];
  state.signature = null;
  state.gcSignature = null;
  state.sendToSafety = true;
  state._csoContact = null;
  // Clear the admin "submit on behalf of" override so the next submission
  // defaults back to the admin themselves (safer default; prevents accidentally
  // submitting another safety form attributed to a foreman who wasn't there).
  state.onBehalfOfForemanId   = null;
  state.onBehalfOfForemanName = '';
  const _onBehalfSel = document.getElementById('onBehalfForemanSelect');
  if (_onBehalfSel) _onBehalfSel.value = '';
  window._signaturePad   = null;
  window._gcSignaturePad = null;

  document.getElementById('typeGrid').innerHTML = '';
  document.getElementById('projectSelect').innerHTML = '<option value="">Select project...</option>';
  const _today = _todayLocal();
  const _dateEl = document.getElementById('foremanDate');
  _dateEl.value = _today;
  _dateEl.max   = _today;

  // Re-fill foreman name from logged-in profile (foreman shouldn't re-type each time)
  const profileName = window.currentProfile?.full_name || '';
  state.foremanName = profileName;
  const nameEl = document.getElementById('foremanName');
  if (nameEl) nameEl.value = profileName;

  // Re-enable nav buttons (disabled during submission)
  const btnNext = document.getElementById('btnNext');
  if (btnNext) { btnNext.disabled = false; btnNext.textContent = 'Next →'; }
  const btnBack = document.getElementById('btnBack');
  if (btnBack) { btnBack.disabled = false; }

  showToast('Ready for next submission', 'success');
  showHomeDashboard();
}

function showToast(msg, type = 'info') {
  const toast = document.getElementById('errorToast');
  if (!toast) return;
  toast.textContent = msg;
  toast.className = `toast toast-${type} visible`;
  if (type !== 'info') setTimeout(() => toast.classList.remove('visible'), 4000);
}

// ══════════════════════════════════════════════════════════════════════════════
//  CERTIFICATIONS PAGE  (Foreman — view all workers + add/view certs)
// ══════════════════════════════════════════════════════════════════════════════

let _certWorkers    = [];   // { id, name, trade, active }[]
let _certTypes      = [];   // { id, name, validity_months }[]
let _certRecords    = [];   // worker_certifications rows (joined with cert type name)
let _certCurrentWId = null; // worker currently open in the modal

// ── Open the page ─────────────────────────────────────────────────────────────
async function openCertificationsPage() {
  showInnerPage('certificationsPage');
  const listEl = document.getElementById('certPageList');
  if (listEl) listEl.innerHTML = '<div class="docs-loading">Loading…</div>';

  const [{ data: workers }, { data: types }, { data: records }] = await Promise.all([
    sbClient.from('workers').select('id, name, trade, active').eq('active', true).order('name'),
    sbClient.from('certification_types').select('id, name, validity_months').order('name'),
    sbClient.from('worker_certifications')
      .select('id, worker_id, cert_type_id, issued_date, expires_date, document_path, notes, certification_types(name, validity_months)')
  ]);

  _certWorkers = workers  || [];
  _certTypes   = types    || [];
  _certRecords = records  || [];

  renderCertWorkersList('');
}

// ── Status helpers ────────────────────────────────────────────────────────────
function _certStatus(expiresIso) {
  if (!expiresIso) return 'unknown';
  const diff = Math.ceil((new Date(expiresIso) - new Date()) / 86400000);
  if (diff < 0)  return 'expired';
  if (diff < 30) return 'expiring';
  return 'valid';
}

function _workerWorstStatus(workerId) {
  const certs = _certRecords.filter(c => c.worker_id === workerId);
  if (!certs.length) return 'none';
  if (certs.some(c => _certStatus(c.expires_date) === 'expired'))  return 'expired';
  if (certs.some(c => _certStatus(c.expires_date) === 'expiring')) return 'expiring';
  return 'valid';
}

// ── Render workers list ───────────────────────────────────────────────────────
function renderCertWorkersList(filter) {
  const listEl = document.getElementById('certPageList');
  if (!listEl) return;

  const q = (filter || '').toLowerCase().trim();
  const list = q
    ? _certWorkers.filter(w =>
        w.name?.toLowerCase().includes(q) || w.trade?.toLowerCase().includes(q))
    : _certWorkers;

  if (!list.length) {
    if (!_certWorkers.length && !q) {
      listEl.innerHTML = `
        <div class="docs-empty" style="text-align:center;padding:24px 16px">
          <div style="font-size:28px;margin-bottom:8px">👥</div>
          <div style="font-weight:700;color:var(--text);margin-bottom:4px">No workers assigned to you yet</div>
          <div style="font-size:13px;color:var(--text-muted)">Ask the admin to assign workers to your account.</div>
        </div>`;
    } else {
      listEl.innerHTML = '<div class="docs-empty">No workers match your search.</div>';
    }
    return;
  }

  const s = v => String(v ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');

  listEl.innerHTML = list.map(w => {
    const status = _workerWorstStatus(w.id);
    const dot = status === 'expired'  ? '🔴' :
                status === 'expiring' ? '🟡' :
                status === 'valid'    ? '🟢' : '⚪';
    const n = _certRecords.filter(c => c.worker_id === w.id).length;
    return `
      <button class="cert-worker-row fdoc-row" type="button"
        onclick="openWorkerCertDetail('${w.id}')">
        <div class="fdoc-row-info" style="flex:1">
          <div class="fdoc-row-title">${s(w.name)}</div>
          <div class="fdoc-row-notes">${s(w.trade || 'No trade set')} · ${n} cert${n !== 1 ? 's' : ''}</div>
        </div>
        <span style="font-size:22px;line-height:1;flex-shrink:0">${dot}</span>
      </button>`;
  }).join('');
}

// ── Open worker detail modal ──────────────────────────────────────────────────
function openWorkerCertDetail(workerId) {
  const worker = _certWorkers.find(w => w.id === workerId);
  if (!worker) return;
  _certCurrentWId = workerId;

  const nameEl  = document.getElementById('certDetailWorkerName');
  const tradeEl = document.getElementById('certDetailWorkerTrade');
  if (nameEl)  nameEl.textContent  = worker.name  || '';
  if (tradeEl) tradeEl.textContent = worker.trade || '';

  _resetAddCertForm();
  _renderWorkerCerts(workerId);

  const modal = document.getElementById('foremanCertModal');
  if (modal) { modal.style.display = 'flex'; document.body.style.overflow = 'hidden'; }
}

function closeWorkerCertModal() {
  const modal = document.getElementById('foremanCertModal');
  if (modal) modal.style.display = 'none';
  document.body.style.overflow = '';
  _certCurrentWId = null;
}

// ── Render cert list inside modal ─────────────────────────────────────────────
function _renderWorkerCerts(workerId) {
  const listEl = document.getElementById('workerCertDetailList');
  if (!listEl) return;

  const certs = _certRecords.filter(c => c.worker_id === workerId);
  if (!certs.length) {
    listEl.innerHTML = '<p style="color:var(--text-muted);font-size:13px;padding:4px 0 10px">No certifications on file.</p>';
    return;
  }

  const s = v => String(v ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');

  listEl.innerHTML = certs.map(c => {
    const status = _certStatus(c.expires_date);
    const badge  = `<span class="cert-badge ${status}">${
      status === 'expired'  ? 'Expired'      :
      status === 'expiring' ? 'Expiring Soon':
      status === 'valid'    ? 'Valid'         : 'No Expiry'}</span>`;
    const typeName = c.certification_types?.name || 'Unknown';
    const issued   = c.issued_date  ? new Date(c.issued_date  + 'T12:00:00').toLocaleDateString('en-CA') : '—';
    const expires  = c.expires_date ? new Date(c.expires_date + 'T12:00:00').toLocaleDateString('en-CA') : '—';
    return `
      <div class="cert-item">
        <div class="cert-item-header">
          <span class="cert-item-type">${s(typeName)}</span>
          ${badge}
        </div>
        <div class="cert-item-meta">Issued: ${issued} · Expires: ${expires}</div>
        ${c.notes ? `<div style="font-size:12px;color:var(--text-muted);margin-top:4px">${s(c.notes)}</div>` : ''}
        ${c.document_path ? `
          <button class="btn-link" onclick="viewForemanCertDoc('${s(c.document_path)}')"
            style="margin-top:6px">📄 View Document</button>` : ''}
      </div>`;
  }).join('');
}

async function viewForemanCertDoc(path) {
  try {
    const { data, error } = await sbClient.storage.from('certifications').createSignedUrl(path, 3600);
    if (error) throw error;
    window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
  } catch (e) {
    showToast('Could not open document: ' + e.message, 'error');
  }
}

// ── Add cert form ─────────────────────────────────────────────────────────────
function _resetAddCertForm() {
  const form = document.getElementById('foremanAddCertForm');
  if (form) form.reset();
  const errEl = document.getElementById('foremanCertError');
  if (errEl) errEl.textContent = '';

  // Reset drag-drop zone
  resetCertDropZone('foremanCertFile', 'foremanCertFileName', 'foremanCertDropZone');

  // Populate type dropdown from cached cert types
  const sel = document.getElementById('foremanCertTypeSelect');
  if (sel) {
    sel.innerHTML = '<option value="">Select type…</option>' +
      _certTypes.map(t =>
        `<option value="${t.id}" data-months="${t.validity_months || ''}">${t.name}</option>`
      ).join('');
  }
}

// ── Drag-and-drop cert file helpers (also defined in admin.js; duplicated for foreman standalone use) ──
function certDropOver(e, zoneId) {
  e.preventDefault();
  document.getElementById(zoneId)?.classList.add('cert-drop-zone--over');
}
function certDropLeave(zoneId) {
  document.getElementById(zoneId)?.classList.remove('cert-drop-zone--over');
}
function certDropDrop(e, inputId, zoneId, labelId) {
  e.preventDefault();
  certDropLeave(zoneId);
  const file = e.dataTransfer?.files?.[0];
  if (!file) return;
  const input = document.getElementById(inputId);
  if (!input) return;
  const dt = new DataTransfer();
  dt.items.add(file);
  input.files = dt.files;
  certFileChosen(input, labelId, zoneId);
}
function certFileChosen(input, labelId, zoneId) {
  const file  = input?.files?.[0];
  const label = document.getElementById(labelId);
  const zone  = document.getElementById(zoneId);
  if (file && label) {
    label.textContent = file.name;
    zone?.classList.add('cert-drop-zone--chosen');
  } else if (label) {
    label.textContent = 'Drop file or click to browse';
    zone?.classList.remove('cert-drop-zone--chosen');
  }
}
function resetCertDropZone(inputId, labelId, zoneId) {
  const input = document.getElementById(inputId);
  if (input) input.value = '';
  const label = document.getElementById(labelId);
  if (label) label.textContent = 'Drop file or click to browse';
  const zone = document.getElementById(zoneId);
  zone?.classList.remove('cert-drop-zone--chosen', 'cert-drop-zone--over');
}

function autoFillForemanCertExpiry() {
  const sel      = document.getElementById('foremanCertTypeSelect');
  const issuedEl = document.getElementById('foremanCertIssued');
  const expEl    = document.getElementById('foremanCertExpires');
  if (!sel || !issuedEl || !expEl) return;

  const months = parseInt(sel.options[sel.selectedIndex]?.dataset?.months || '0');
  const issued  = issuedEl.value;
  if (!months || !issued) return;

  const d = new Date(issued + 'T12:00:00');
  d.setMonth(d.getMonth() + months);
  expEl.value = d.toISOString().slice(0, 10);
}

async function saveForemanCert() {
  const errEl = document.getElementById('foremanCertError');
  if (errEl) errEl.textContent = '';

  const typeId  = document.getElementById('foremanCertTypeSelect')?.value;
  const issued  = document.getElementById('foremanCertIssued')?.value;
  const expires = document.getElementById('foremanCertExpires')?.value;
  const notes   = document.getElementById('foremanCertNotes')?.value.trim();
  const fileEl  = document.getElementById('foremanCertFile');
  const file    = fileEl?.files?.[0] || null;

  if (!typeId) { if (errEl) errEl.textContent = 'Please select a certification type.'; return; }
  if (!issued) { if (errEl) errEl.textContent = 'Please enter the issued date.'; return; }
  if (!_certCurrentWId) return;

  const btn = document.getElementById('foremanCertSaveBtn');
  if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }

  try {
    let docPath = null, docUrl = null;

    if (file) {
      const ext  = file.name.split('.').pop().toLowerCase();
      const path = `worker_${_certCurrentWId}/cert_${typeId}_${Date.now()}.${ext}`;
      const { error: upErr } = await sbClient.storage
        .from('certifications').upload(path, file, { contentType: file.type, upsert: false });
      if (upErr) throw new Error('File upload failed: ' + upErr.message);

      const { data: urlData } = await sbClient.storage
        .from('certifications').createSignedUrl(path, 3600);
      docPath = path;
      docUrl  = urlData?.signedUrl || null;
    }

    const { data: newRec, error: insErr } = await sbClient
      .from('worker_certifications')
      .insert({
        worker_id:     _certCurrentWId,
        cert_type_id:  typeId,
        issued_date:   issued,
        expires_date:  expires || null,
        document_url:  docUrl,
        document_path: docPath,
        notes:         notes || null
      })
      .select('id, worker_id, cert_type_id, issued_date, expires_date, document_path, notes, certification_types(name, validity_months)')
      .single();

    if (insErr) throw new Error(insErr.message);

    _certRecords.push(newRec);
    showToast('Certification saved!', 'success');
    _resetAddCertForm();
    _renderWorkerCerts(_certCurrentWId);
    renderCertWorkersList(document.getElementById('certSearch')?.value || '');

  } catch (e) {
    if (errEl) errEl.textContent = e.message;
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = 'Save Certification'; }
  }
}

// ── User Avatar (deterministic image per user) ───────────────────────────────
const TOTAL_AVATARS = 18;

function avatarNumberForUser(userId) {
  if (!userId) return 1;
  let sum = 0;
  for (let i = 0; i < userId.length; i++) sum += userId.charCodeAt(i);
  return (sum % TOTAL_AVATARS) + 1;        // 1..18
}

function avatarUrl(userId) {
  const n = avatarNumberForUser(userId);
  return `assets/avatars/avatar-${String(n).padStart(2, '0')}.png`;
}

// Apply user avatar to an element. Keeps the initial letter as a fallback.
// Only swaps to image once it has loaded successfully.
function applyUserAvatar(el) {
  if (!el) return;
  const userId = window.currentUser?.id;
  if (!userId) return;
  const url = avatarUrl(userId);
  const test = new Image();
  test.onload = () => {
    el.style.backgroundImage = `url('${url}')`;
    el.classList.add('has-avatar');
  };
  // If the image fails (e.g. 404), do nothing — the initial letter remains visible.
  test.src = url;
}

// ── User Menu Dropdown ───────────────────────────────────────────────────────
function toggleUserMenu(ev) {
  if (ev) ev.stopPropagation();
  const menu = document.getElementById('userMenu');
  const btn  = document.getElementById('dashAvatarBtn');
  if (!menu) return;

  const isOpen = menu.classList.contains('open');
  if (isOpen) {
    closeUserMenu();
    return;
  }

  // Populate menu header from current profile
  const name   = window.currentProfile?.full_name || 'User';
  const email  = window.currentUser?.email || '';
  const initial = (name.trim()[0] || '?').toUpperCase();
  document.getElementById('userMenuName').textContent  = name;
  document.getElementById('userMenuEmail').textContent = email;
  const menuAv = document.getElementById('userMenuAvatar');
  if (menuAv) {
    menuAv.textContent = initial;
    applyUserAvatar(menuAv);
  }

  // Show Admin Panel item only for admins
  const adminItem = document.getElementById('userMenuAdminBtn');
  if (adminItem) {
    adminItem.style.display = (window.currentProfile?.role === 'admin') ? '' : 'none';
  }

  menu.classList.add('open');
  menu.setAttribute('aria-hidden', 'false');
  btn?.setAttribute('aria-expanded', 'true');

  // Close on outside click / Escape
  setTimeout(() => {
    document.addEventListener('click', _userMenuOutsideHandler);
    document.addEventListener('keydown', _userMenuEscHandler);
  }, 0);
}

function closeUserMenu() {
  const menu = document.getElementById('userMenu');
  const btn  = document.getElementById('dashAvatarBtn');
  if (!menu) return;
  menu.classList.remove('open');
  menu.setAttribute('aria-hidden', 'true');
  btn?.setAttribute('aria-expanded', 'false');
  document.removeEventListener('click', _userMenuOutsideHandler);
  document.removeEventListener('keydown', _userMenuEscHandler);
}

function _userMenuOutsideHandler(ev) {
  const menu = document.getElementById('userMenu');
  if (menu && !menu.contains(ev.target) && !document.getElementById('dashAvatarBtn').contains(ev.target)) {
    closeUserMenu();
  }
}

function _userMenuEscHandler(ev) {
  if (ev.key === 'Escape') closeUserMenu();
}

// ── Change Password Modal ─────────────────────────────────────────────────────
function openChangePasswordModal() {
  document.getElementById('cpNewPassword').value  = '';
  document.getElementById('cpNewPassword2').value = '';
  document.getElementById('cpError').textContent   = '';
  document.getElementById('cpSuccess').textContent = '';
  document.getElementById('changePasswordModal').style.display = 'flex';
  document.body.style.overflow = 'hidden';
  setTimeout(() => document.getElementById('cpNewPassword')?.focus(), 50);
}

function closeChangePasswordModal() {
  document.getElementById('changePasswordModal').style.display = 'none';
  document.body.style.overflow = '';
}

async function handleChangePassword() {
  const errEl  = document.getElementById('cpError');
  const succEl = document.getElementById('cpSuccess');
  const btn    = document.getElementById('cpSaveBtn');
  errEl.textContent  = '';
  succEl.textContent = '';

  const p1 = document.getElementById('cpNewPassword').value;
  const p2 = document.getElementById('cpNewPassword2').value;

  if (p1.length < 6) { errEl.textContent = 'Password must be at least 6 characters.'; return; }
  if (p1 !== p2)     { errEl.textContent = 'The two passwords do not match.'; return; }

  btn.disabled = true; btn.textContent = 'Saving…';

  const { error } = await sbClient.auth.updateUser({ password: p1 });

  if (error) {
    errEl.textContent = error.message || 'Could not update password.';
    btn.disabled = false; btn.textContent = 'Save new password';
    return;
  }

  succEl.textContent = 'Password updated!';
  setTimeout(() => {
    closeChangePasswordModal();
    btn.disabled = false; btn.textContent = 'Save new password';
  }, 1200);
}

// ── Production / Time-sheet dashboard sync ────────────────────────────────────
// Both Production Report and Weekly Timesheet are submitted as a weekly batch
// (Mon-Sun in the form). The admin dashboard reads from per-day rows, so we
// expand the form into one row per day here, called right after submissions
// insert. These run best-effort — a failure here doesn't block PDF/email.

// Returns a Date set to the Monday of the week containing dateIso (YYYY-MM-DD).
function _weekStartFor(dateIso) {
  // Parse as local-time midnight to avoid TZ off-by-one
  const [y, m, d] = (dateIso || new Date().toISOString().slice(0, 10)).split('-').map(Number);
  const date = new Date(y, (m || 1) - 1, d || 1);
  const dow  = date.getDay();           // 0=Sun, 1=Mon, ..., 6=Sat
  const offset = dow === 0 ? -6 : 1 - dow;
  date.setDate(date.getDate() + offset);
  return date;
}

// dayIndex: 0=Mon, 1=Tue, ..., 6=Sun → YYYY-MM-DD
function _dateFromWeekStart(weekStart, dayIndex) {
  const d = new Date(weekStart);
  d.setDate(d.getDate() + dayIndex);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

async function syncProductionReportToDb(s, submissionId) {
  // Resolve project name → id
  const { data: project } = await sbClient.from('projects')
    .select('id').eq('name', s.project).maybeSingle();
  if (!project) { console.warn('syncProductionReport: project not found', s.project); return; }

  const weekStart = _weekStartFor(s.date);
  const DAY_KEYS  = ['mon','tue','wed','thu','fri','sat','sun'];
  const items = Array.isArray(s.fields?.production?.items) ? s.fields.production.items : [];
  if (!items.length) return;

  // Load catalog → name → { id, category }
  const { data: mtypes } = await sbClient.from('material_types').select('id, name, category');
  const typeByName = {};
  (mtypes || []).forEach(t => { typeByName[t.name] = t; });

  // Per-day totals (blocks_installed / bricks_installed kept for back-compat
  // with the current dashboard; per-type detail goes into production_line_items).
  const dayBlocks = {}, dayBricks = {}, dayFirestop = {}, dayHas = {};
  DAY_KEYS.forEach(d => { dayBlocks[d] = 0; dayBricks[d] = 0; dayFirestop[d] = 0; dayHas[d] = false; });

  items.forEach(item => {
    const cat = typeByName[item.type]?.category;
    DAY_KEYS.forEach(day => {
      const q = parseFloat(item.qty?.[day]) || 0;
      if (q > 0) {
        dayHas[day] = true;
        if (cat === 'block') dayBlocks[day] += q;
        else if (cat === 'brick') dayBricks[day] += q;
        else if (cat === 'firestop') dayFirestop[day] += q;   // linear feet
      }
    });
  });

  const dayRows = DAY_KEYS
    .map((day, idx) => ({ day, idx }))
    .filter(d => dayHas[d.day])
    .map(d => ({
      date: _dateFromWeekStart(weekStart, d.idx),
      dayKey: d.day,
      payload: {
        project_id:       project.id,
        foreman_id:       _effectiveForemanId(),
        foreman_name:     _effectiveForemanName(),
        report_date:      _dateFromWeekStart(weekStart, d.idx),
        bricks_installed: Math.round(dayBricks[d.day]),
        blocks_installed: Math.round(dayBlocks[d.day]),
        firestop_lf:      Math.round(dayFirestop[d.day] * 10) / 10,
        notes:            (s.fields?.delays || '').trim() || null,
        submission_id:    submissionId || null,
      },
    }));

  if (!dayRows.length) return;

  // Upsert report rows, get IDs back. Conflict key includes foreman_id so two
  // foremen reporting the same project/day each keep their own row (the old
  // (project_id, report_date) key made the second overwrite the first).
  const { data: upserted, error } = await sbClient.from('production_reports')
    .upsert(dayRows.map(r => r.payload), { onConflict: 'project_id,report_date,foreman_id' })
    .select('id, report_date');
  if (error) { console.warn('production_reports upsert failed:', error.message); return; }

  const reportIdByDate = {};
  (upserted || []).forEach(r => { reportIdByDate[r.report_date] = r.id; });

  // Clean re-sync: clear existing line items for these reports, then re-insert
  const reportIds = Object.values(reportIdByDate);
  if (reportIds.length) {
    await sbClient.from('production_line_items').delete().in('production_report_id', reportIds);
  }

  // One line item per material per day. bricklayers/helpers columns still
  // exist in the DB for legacy data but are no longer collected in the form
  // (labor cost comes from time_entries / sub rates, headcount was redundant).
  const lineItems = [];
  dayRows.forEach(r => {
    const reportId = reportIdByDate[r.date];
    if (!reportId) return;
    items.forEach(item => {
      const mt = typeByName[item.type];
      if (!mt) return;
      const qty = parseFloat(item.qty?.[r.dayKey]) || 0;
      if (qty <= 0) return;
      lineItems.push({
        production_report_id: reportId,
        material_type_id:     mt.id,
        quantity:             qty,
      });
    });
  });

  if (lineItems.length) {
    const { error: liErr } = await sbClient.from('production_line_items')
      .upsert(lineItems, { onConflict: 'production_report_id,material_type_id' });
    if (liErr) console.warn('production_line_items upsert failed:', liErr.message);
  }
}

// ── Sick-day yearly allowance ─────────────────────────────────────────────────
// Each in-house worker gets 5 PAID sick days per calendar year. A sick day
// beyond that is still recorded in sick_days (absence tracking — the admin tab
// shows it in red) but pays nothing: no 8h time_entries row, 0 hrs on the PDF.
// This runs at submit time and stamps `cell.sick_unpaid = true` on the
// over-allowance cells, so the PDF generator and syncTimesheetToDb both see it.
const SICK_DAYS_PER_YEAR = 5;

async function _annotateUnpaidSickDays(s) {
  if (s.submissionType !== 'Weekly Timesheet') return;
  const employees = Array.isArray(s.fields?.timesheet) ? s.fields.timesheet : [];
  const DAY_KEYS  = ['mon','tue','wed','thu','fri','sat','sun'];
  const weekStart = _weekStartFor(s.date);

  const entries = [];   // { emp, cell, date }
  employees.forEach(emp => {
    DAY_KEYS.forEach((day, idx) => {
      const cell = emp?.days?.[day];
      if (!cell) return;
      delete cell.sick_unpaid;   // clear stale flags from drafts / resubmits
      if (cell.sick) entries.push({ emp, cell, date: _dateFromWeekStart(weekStart, idx) });
    });
  });
  if (!entries.length) return;

  // Resolve worker ids the same way the sync does (dropdown id, else name).
  const names = [...new Set(entries
    .filter(e => !e.emp.worker_id && (e.emp.name || '').trim())
    .map(e => e.emp.name.trim()))];
  const idByName = {};
  if (names.length) {
    const { data: ws } = await sbClient.from('workers').select('id, name').in('name', names);
    (ws || []).forEach(w => { idByName[w.name.toLowerCase()] = w.id; });
  }
  const workerIdOf = emp =>
    (emp.worker_id || '').trim() || idByName[(emp.name || '').trim().toLowerCase()] || null;

  const ids = [...new Set(entries.map(e => workerIdOf(e.emp)).filter(Boolean))];
  if (!ids.length) return;

  const year = weekStart.getFullYear();
  const { data: rows, error } = await sbClient.from('sick_days')
    .select('worker_id, sick_date, portion')
    .in('worker_id', ids)
    .gte('sick_date', year + '-01-01')
    .lte('sick_date', year + '-12-31');
  if (error) throw error;

  // Exclude the dates in THIS submission from the "used" tally — a resubmitted
  // week would otherwise count itself and flip its own days to unpaid.
  const submittedDates = {};   // worker_id → Set of ISO dates
  entries.forEach(e => {
    const wid = workerIdOf(e.emp); if (!wid) return;
    (submittedDates[wid] = submittedDates[wid] || new Set()).add(e.date);
  });
  const used = {};
  (rows || []).forEach(r => {
    if (submittedDates[r.worker_id]?.has(r.sick_date)) return;
    used[r.worker_id] = (used[r.worker_id] || 0) + (Number(r.portion) || 1);
  });

  // Chronological within the week; each paid day consumes one allowance unit.
  entries.sort((a, b) => a.date.localeCompare(b.date));
  entries.forEach(e => {
    const wid = workerIdOf(e.emp); if (!wid) return;
    const u = used[wid] || 0;
    if (u + 1 > SICK_DAYS_PER_YEAR) e.cell.sick_unpaid = true;
    else used[wid] = u + 1;
  });
}

async function syncTimesheetToDb(s, submissionId) {
  const { data: project } = await sbClient.from('projects')
    .select('id').eq('name', s.project).maybeSingle();
  if (!project) { console.warn('syncTimesheet: project not found', s.project); return; }

  const weekStart = _weekStartFor(s.date);
  const DAY_KEYS  = ['mon','tue','wed','thu','fri','sat','sun'];
  const employees = Array.isArray(s.fields?.timesheet) ? s.fields.timesheet : [];

  // Prefer the worker_id snapshotted at form-fill time (the new dropdown sets it
  // directly). Fall back to a name lookup for older drafts that still carry only
  // a name — case-insensitive against the workers table.
  const idsSeen = [...new Set(employees.map(e => (e?.worker_id || '').trim()).filter(Boolean))];
  const namesForLookup = [...new Set(employees
    .filter(e => !e?.worker_id && (e?.name || '').trim())
    .map(e => e.name.trim()))];
  const workerByName = {};
  if (namesForLookup.length) {
    const { data: workers } = await sbClient.from('workers').select('id, name').in('name', namesForLookup);
    (workers || []).forEach(w => { workerByName[w.name.toLowerCase()] = w.id; });
  }
  if (!idsSeen.length && !Object.keys(workerByName).length) return;

  const rows = [];
  const sickRows = [];
  employees.forEach(emp => {
    const workerId = (emp?.worker_id || '').trim() ||
                     workerByName[(emp?.name || '').trim().toLowerCase()];
    if (!workerId) return;
    DAY_KEYS.forEach((day, idx) => {
      const cell = emp?.days?.[day] || {};
      if (cell.sick) {
        // Sick day → sick_days table (the admin Sick Days tab reads it, so it
        // counts immediately)...
        sickRows.push({
          worker_id:   workerId,
          worker_name: (emp?.name || '').trim() || 'Unknown',
          sick_date:   _dateFromWeekStart(weekStart, idx),
          portion:     1,
          note:        'From timesheet — ' + _effectiveForemanName() +
                       (cell.sick_unpaid ? ' (UNPAID — 5-day allowance used)' : ''),
        });
        // Over the 5/year allowance (flagged by _annotateUnpaidSickDays):
        // absence recorded above, but no paid hours and no labor cost.
        if (cell.sick_unpaid) return;
        // ...and it's PAID: 8 regular hours flow into time_entries so the
        // dashboard / project P&L labor cost includes the sick pay.
        rows.push({
          worker_id:      workerId,
          project_id:     project.id,
          foreman_id:     _effectiveForemanId(),
          foreman_name:   _effectiveForemanName(),
          work_date:      _dateFromWeekStart(weekStart, idx),
          regular_hours:  8,
          overtime_hours: 0,
          hours_worked:   8,
          break_minutes:  0,
          submission_id:  submissionId || null,
        });
        return;
      }
      const reg = parseFloat(cell.r)  || 0;
      const ot  = parseFloat(cell.ot) || 0;
      const total = reg + ot;
      if (total > 0) {
        rows.push({
          worker_id:      workerId,
          project_id:     project.id,
          foreman_id:     _effectiveForemanId(),
          foreman_name:   _effectiveForemanName(),
          work_date:      _dateFromWeekStart(weekStart, idx),
          regular_hours:  reg,
          overtime_hours: ot,
          hours_worked:   total,
          break_minutes:  0,
          submission_id:  submissionId || null,
        });
      }
    });
  });

  if (rows.length) {
    const { error } = await sbClient.from('time_entries')
      .upsert(rows, { onConflict: 'worker_id,work_date' });
    if (error) console.warn('time_entries upsert failed:', error.message);
  }
  if (sickRows.length) {
    // Upsert on (worker_id, sick_date) so re-submitting the same week never
    // duplicates a sick day (unique index sick_days_worker_date_uniq).
    const { error } = await sbClient.from('sick_days')
      .upsert(sickRows, { onConflict: 'worker_id,sick_date' });
    if (error) console.warn('sick_days upsert failed:', error.message);
  }
}

// Compress a photo File → JPEG blob (max 1600px, q0.82) before uploading to
// Storage — phone camera shots are 4-8 MB each and would blow up the bucket.
function _compressPhotoForUpload(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const MAX = 1600;
      const scale = Math.min(1, MAX / Math.max(img.width, img.height));
      const cv = document.createElement('canvas');
      cv.width  = Math.round(img.width  * scale);
      cv.height = Math.round(img.height * scale);
      cv.getContext('2d').drawImage(img, 0, 0, cv.width, cv.height);
      URL.revokeObjectURL(url);
      cv.toBlob(b => resolve(b || file), 'image/jpeg', 0.82);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}

// ══════════════════════════════════════════════════════════════════════════════
//  Extra Work Order — write the parent + labour + materials rows
//  Foreman submits WHO + HOURS + WHAT-MATERIAL + QTY. All rates / amounts /
//  burden / overhead / fee start at 0 — admin fills them during finalisation.
// ══════════════════════════════════════════════════════════════════════════════
async function syncExtraWorkOrderToDb(s, submissionId) {
  const { data: project } = await sbClient.from('projects')
    .select('id, burden_pct').eq('name', s.project).maybeSingle();
  if (!project) { console.warn('syncEwo: project not found', s.project); return; }

  // Resolve EWO# — prefer the one peeked at render time, otherwise call the RPC.
  let ewoNumber = Number(s._ewoNextNumber) || 0;
  if (!ewoNumber) {
    const { data: n } = await sbClient.rpc('next_ewo_number', { p_project_id: project.id });
    ewoNumber = Number(n) || 0;
  }
  if (!ewoNumber) { console.warn('syncEwo: could not resolve EWO number'); return; }

  // Photos attached on step 3 → Storage (form-submissions, under the foreman's
  // own folder to satisfy the bucket's SELECT policy). The paths land in
  // photo_urls so the admin tab shows them and the FINAL priced PDF embeds
  // them. Best-effort per photo — one bad file never blocks the EWO itself.
  const photoPaths = [];
  const photoFiles = Array.isArray(s.photos) ? s.photos : [];
  for (let pi = 0; pi < photoFiles.length; pi++) {
    try {
      const blob = await _compressPhotoForUpload(photoFiles[pi]);
      const path = `${window.currentUser.id}/ewo/EWO${ewoNumber}-${Date.now()}-${pi + 1}.jpg`;
      const { error: upErr } = await sbClient.storage
        .from('form-submissions')
        .upload(path, blob, { contentType: 'image/jpeg', upsert: false });
      if (upErr) { console.warn('EWO photo upload failed:', upErr.message); continue; }
      photoPaths.push(path);
    } catch (phErr) { console.warn('EWO photo skipped:', phErr); }
  }

  // Parent row — all $ fields start at 0. Burden_pct seeded from project so
  // admin's starting point matches the project's standard burden.
  // Signatures stored on the parent so admin can regenerate the FINAL priced
  // PDF later (the foreman PDF in Storage has no rates / totals).
  const parent = {
    ewo_number:      ewoNumber,
    project_id:      project.id,
    foreman_id:      _effectiveForemanId(),
    foreman_name:    _effectiveForemanName(),
    submission_id:   submissionId || null,
    week_ending:     s.fields?.week_ending || null,
    work_description: (s.fields?.work_description || '').trim() || null,
    status:          'submitted',
    burden_pct:      Number(project.burden_pct) || 0,
    overhead_pct:    10,
    fee_pct:         5,
    small_tool_pct:  18,
    total_labour:    0,
    total_burden:    0,
    total_material:  0,
    subtotal:        0,
    overhead_amount: 0,
    fee_amount:      0,
    total_amount:    0,
    foreman_signature_b64: s.signature   || null,
    gc_signature_b64:      s.gcSignature || null,
    gc_name:               (s.fields?.gc_name || '').trim() || null,
    photo_urls:            photoPaths,
  };

  const { data: inserted, error: insErr } = await sbClient
    .from('extra_work_orders')
    .insert(parent)
    .select('id, ewo_number')
    .single();
  if (insErr) { console.error('extra_work_orders insert failed:', insErr); return; }
  const ewoId = inserted.id;

  // Labour rows — record who worked and the hour breakdown. No rate, no amount.
  const labourRows = (s.fields?.ewo_labour || [])
    .filter(r => r.worker_id || r.worker_name)
    .map((r, i) => ({
      ewo_id:       ewoId,
      worker_id:    r.worker_id || null,
      worker_name:  r.worker_name || '(unnamed)',
      worker_trade: r.worker_trade || null,
      hourly_rate:  0,          // admin sets at finalisation
      reg_hours:    Number(r.reg_hours) || 0,
      ot_hours:     Number(r.ot_hours)  || 0,
      td_hours:     Number(r.td_hours)  || 0,
      amount:       0,
      line_order:   i,
    }))
    .filter(r => r.reg_hours || r.ot_hours || r.td_hours);
  if (labourRows.length) {
    const { error } = await sbClient.from('extra_work_order_labour').insert(labourRows);
    if (error) console.warn('ewo labour insert failed:', error.message);
  }

  // Material rows — only those the foreman actually filled (qty > 0, or a custom
  // item with a description even if qty wasn't entered)
  const materialRows = (s.fields?.ewo_materials || [])
    .filter(m => (Number(m.qty) || 0) > 0 || (!m.from_list && (m.description || '').trim()))
    .map((m, i) => ({
      ewo_id:      ewoId,
      po_number:   null,                     // admin fills
      vendor:      null,                     // admin fills
      description: m.description || '(no description)',
      qty:         Number(m.qty) || 0,
      unit:        m.unit || null,
      rate:        0,                        // admin sets at finalisation
      amount:      0,
      is_auto:     false,
      line_order:  i,
    }));
  if (materialRows.length) {
    const { error } = await sbClient.from('extra_work_order_materials').insert(materialRows);
    if (error) console.warn('ewo materials insert failed:', error.message);
  }

  // ── Upload EWO photos to Storage so admin Final PDF can include them ────────
  // Photos are already embedded in the foreman's PDF, but we also store them
  // individually so generateFinalEwoPDFForAdmin() can fetch and re-embed them.
  if (s.photos && s.photos.length > 0 && window.currentUser) {
    try {
      const photoStoragePaths = [];
      for (let i = 0; i < Math.min(s.photos.length, 9); i++) {
        const file = s.photos[i];
        const ext  = (file.type === 'image/png') ? 'png' : 'jpg';
        const path = `ewo-photos/${ewoId}/photo-${i}.${ext}`;
        const { error: upErr } = await sbClient.storage
          .from('form-submissions')
          .upload(path, file, { contentType: file.type, upsert: false });
        if (!upErr) photoStoragePaths.push(path);
        else console.warn(`ewo photo ${i} upload failed:`, upErr.message);
      }
      if (photoStoragePaths.length > 0) {
        await sbClient.from('extra_work_orders')
          .update({ photo_urls: photoStoragePaths })
          .eq('id', ewoId);
      }
    } catch (photoErr) {
      console.warn('ewo photo upload error:', photoErr);
    }
  }

  // ── Fire notification email to admin (best-effort, non-blocking) ───────────
  try {
    await sbClient.functions.invoke('notify-extra-work', {
      body: {
        ewo_id:       ewoId,
        ewo_number:   inserted.ewo_number,
        project_name: s.project,
        foreman_name: _effectiveForemanName(),
        week_ending:  s.fields?.week_ending || null,
        labour_count: labourRows.length,
        material_count: materialRows.length,
      }
    });
  } catch (e) { console.warn('notify-extra-work invoke failed:', e); }
}
